(() => {
  const mdT = (key, variables) => window.FoxTheme?.i18n?.t(key, variables) || key;
  const mdRoot = (path) => {
    const root = window.FoxTheme?.i18n?.rootUrl || '/';
    return root.replace(/\/$/, '') + '/' + String(path || '').replace(/^\//, '');
  };
  const MOUNT_ID = 'ai-layout-app';
  const VERSION = 'ai-layout-creator@2026-10-23-creator-ux';
  // Vision endpoint via Shopify App Proxy (no CORS)
  const VISION_URL = '/apps/vision-extract';
  // How long the page waits for a photo reading (the Worker gives the reader 60 s).
  const ANALYZE_TIMEOUT_MS = 75000;
  // The layout comes from one place: the Worker's optimise action, which the
  // layout engine answers (internal-tools/engine). The greedy per-item match, the
  // client packers and the generate_url relay are retired
  // (internal-tools/engine/RETIRING.md).

  // Size fields and units: the rule that spots a size typed in centimetres is
  // shared with the Drawer Builder. It lives in assets/md-size-units.js, which
  // sections/ai-layout-creator.liquid loads before this file. Read when needed:
  // if it is missing the size step shows no hint and works as before.
  const sizeUnits = () => window.mdSizeUnits || null;

  // "Describe your drawer" (sections/describe-your-drawer.liquid) mounts this
  // wizard with data-input-mode="text": the visitor types what is in the drawer
  // instead of uploading a photo. The reading is the only step that differs:
  // the same items, the same optimise call and the same layout engine follow.
  // Both pages share the reader contracts and the same deterministic engine.
  const TEXT_MODE = (() => {
    try {
      const mount = typeof document !== 'undefined' ? document.getElementById(MOUNT_ID) : null;
      return !!(mount && mount.getAttribute('data-input-mode') === 'text');
    } catch (_) {
      return false;
    }
  })();
  const SURFACE = TEXT_MODE ? 'describe-drawer' : '';
  const DESCRIBE_MAX_CHARS = 800;
  const DESCRIBE_MIN_CHARS = 3;

  // md-ai-layout-height-start
  const STANDARD_DRAWER_HEIGHT_MM = 45;
  const MIN_DRAWER_HEIGHT_MM = 20;
  const MAX_MODULE_HEIGHT_MM = 150;
  const MAX_DRAWER_INTERIOR_HEIGHT_MM = 2000;

  function normaliseDrawerInteriorHeightMm(value) {
    const raw = String(value == null ? '' : value).trim();
    if (!/^\d+(?:\.\d+)?$/.test(raw)) return null;
    const heightMm = Number(raw);
    if (!Number.isFinite(heightMm) || heightMm < MIN_DRAWER_HEIGHT_MM || heightMm > MAX_DRAWER_INTERIOR_HEIGHT_MM) {
      return null;
    }
    return Math.round(heightMm * 10) / 10;
  }

  function normaliseDrawerHeightMm(value) {
    const interiorHeightMm = normaliseDrawerInteriorHeightMm(value);
    if (interiorHeightMm == null) return null;
    // Drawer clearance is not a request to purchase resized trays. Finished
    // custom height is a separate opt-in in the Drawer Builder.
    return STANDARD_DRAWER_HEIGHT_MM;
  }

  // Validate the handoff without interpreting drawer clearance as tray height.
  // Preserve any explicit manufacturing intent already present in saved hashes.
  function reconcileBuilderHashHeight(hash, heightMm) {
    const targetHeightMm = normaliseDrawerHeightMm(heightMm);
    const rawHash = String(hash == null ? '' : hash).trim().replace(/^#/, '');
    if (targetHeightMm == null || !rawHash) return null;
    const parts = rawHash.split('&').filter(Boolean);
    if (!/^\d+(?:\.\d+)?x\d+(?:\.\d+)?$/.test(parts[0] || '')) return null;
    // Do not overwrite any intent already recorded in a restored hash. New
    // solver layouts contain no height request and keep their authored parts.
    return parts.join('&');
  }
  // md-ai-layout-height-end

  // md-ai-request-link-start
  // The builder link names the run that produced its layout (?ai=<requestUuid>).
  // The builder stamps it on the cart as __drawer_ai_request_<buildId>, so an
  // order ties back to this run and its eval case: what the AI proposed next
  // to what was paid for. A malformed id is left out and the link still works.
  const AI_REQUEST_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

  function builderHandoffUrl(origin, builderPath, requestUuid, hash) {
    const id = String(requestUuid == null ? '' : requestUuid).trim().toLowerCase();
    const query = AI_REQUEST_ID_RE.test(id) ? `?ai=${id}` : '';
    return `${origin}${builderPath}${query}#${hash}`;
  }
  // md-ai-request-link-end

  // md-ai-custom-design-context-start
  // Plain contact fields only: no photo bytes, email or marketing consent.
  function customDesignContext(input, layoutLink) {
    const busy = !!(input.optimising || input.optimiseDirty || input.analyzing);
    const run = String(input.requestUuid || '').trim().toLowerCase();
    const items = Array.isArray(input.items) ? input.items : [];
    const inventory = items.slice(0, 150).map(item => {
      const name = String(item.name || '').slice(0, 200);
      const detail = String(item.detail || '').slice(0, 200);
      const size = [item.approxLenMM, item.approxWidMM].filter(n => Number(n) > 0).join(' × ');
      return `${name}: ${Number(item.qty) > 0 ? item.qty : 1}${size ? `; ${size} mm` : ''}${detail ? `; ${detail}` : ''}`;
    }).join('\n').slice(0, 16000);
    const missed = input.optimiser && input.optimiser.stats && input.optimiser.stats.uncovered;
    return {
      run: AI_REQUEST_ID_RE.test(run) ? run : '',
      dimensions: [input.L, input.W, input.H].map(n => Number(n) > 0 ? String(n) : '?').join(' × '),
      inventory,
      description: String(input.description || '').slice(0, 800),
      layout: busy ? '' : String(layoutLink || ''),
      option: ['essentialist', 'maximiser', 'use-case'].indexOf(input.archetype) >= 0 ? input.archetype : '',
      status: busy ? 'Layout pending; use the supplied inventory' : layoutLink
        ? `Layout ready${Array.isArray(missed) && missed.length ? `; not placed: ${missed.join(', ')}` : ''}`
        : 'No layout available; a manual design is requested'
    };
  }
  // md-ai-custom-design-context-end

  // AI-first loading: rotating "thinking" lines instead of a fake % bar.
  const AI_ANALYZE_MSGS = [
    mdT('modu.builder_js.thinking_look'),
    mdT('modu.builder_js.thinking_measure'),
    mdT('modu.builder_js.thinking_identify'),
    mdT('modu.builder_js.thinking_match')
  ];
  const AI_DESCRIBE_MSGS = [
    mdT('modu.builder_js.describe_thinking_read'),
    mdT('modu.builder_js.describe_thinking_match'),
    mdT('modu.builder_js.describe_thinking_design')
  ];
  const AI_GENERATE_MSGS = [
    mdT('modu.builder_js.thinking_design'),
    mdT('modu.builder_js.thinking_pack'),
    mdT('modu.builder_js.thinking_fill'),
    mdT('modu.builder_js.thinking_finish')
  ];


  // Cross-fade a thinking line in place (the overlay text node persists
  // across the ticker; render() is not re-run mid-analyze/generate).
  function setThinkingLine(id, text) {
    const node = document.getElementById(id);
    if (!node || node.textContent === text) return;
    node.style.opacity = '0';
    setTimeout(() => { node.textContent = text; node.style.opacity = '1'; }, 180);
  }

  // Generate a session UUID for request tracking
  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // ============================================================================
  // Session Management - URL-based session ID for state isolation
  // ============================================================================
  
  /**
   * Get or create a session ID from URL query params.
   * If no session param exists, generates a new UUID and updates URL.
   * Returns the session ID (short 8-char version for display, full UUID for storage).
   */
  function getOrCreateSessionId() {
    const urlParams = new URLSearchParams(window.location.search);
    let sessionId = urlParams.get('session');
    
    if (!sessionId) {
      // Generate new session and update URL without reload
      sessionId = generateUUID();
      urlParams.set('session', sessionId);
      const newUrl = window.location.pathname + '?' + urlParams.toString() + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'session:created', { sessionId });
    } else {
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'session:restored', { sessionId });
    }
    
    return sessionId;
  }
  
  /**
   * Start a new session: clear current state and redirect to fresh URL
   */
  function startNewSession() {
    const newSessionId = generateUUID();
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('session', newSessionId);
    const newUrl = window.location.pathname + '?' + urlParams.toString();
    // Navigate to new session (this clears in-memory state)
    window.location.href = newUrl;
  }
  
  /**
   * Get display-friendly short session ID (first 8 chars)
   */
  function getShortSessionId(sessionId) {
    return sessionId ? sessionId.substring(0, 8) : '';
  }
  
  // Current session ID (initialized on load)
  let currentSessionId = null;

  // md-ai-layout-events-start
  // Consent-safe, allowlisted measurement for the AI-layout journey. The
  // browser contract deliberately lives inside this small dependency-injected
  // block so tests can execute it without booting the photo/solver UI.
  function createAiLayoutAnalytics(win, storage, storageKey, makeUuid, eventSource) {
    // The describe page reports the same four events under its own source.
    const SOURCE = eventSource === 'describe_drawer' ? 'describe_drawer' : 'ai_layout_creator';
    const EVENT_NAMES = {
      ai_layout_analysis_started: true,
      ai_layout_analysis_completed: true,
      ai_layout_generated: true,
      ai_layout_builder_handoff: true,
      ai_layout_lead_submitted: true
    };
    const EVENT_OUTCOMES = {
      ai_layout_analysis_started: { started: true },
      ai_layout_analysis_completed: { success: true, error: true },
      ai_layout_generated: { success: true },
      ai_layout_builder_handoff: { success: true },
      ai_layout_lead_submitted: { success: true }
    };
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    let ledger = { pageSessionId: '', fired: {} };
    let loaded = false;

    // Load/generate the analytics ledger lazily, only after analytics consent.
    // This avoids creating or reading an analytics identifier while consent is off.
    function load() {
      if (loaded) return;
      loaded = true;
      try {
        const saved = JSON.parse(storage.getItem(storageKey) || '{}');
        const fresh = saved && Number.isFinite(Number(saved.savedAt)) &&
          (Date.now() - Number(saved.savedAt)) < 2 * 60 * 60 * 1000;
        if (!fresh && saved && Object.keys(saved).length && typeof storage.removeItem === 'function') {
          storage.removeItem(storageKey);
        }
        if (fresh && UUID_RE.test(String(saved.pageSessionId || ''))) {
          ledger.pageSessionId = String(saved.pageSessionId);
        }
        if (fresh && saved.fired && typeof saved.fired === 'object') {
          Object.keys(saved.fired).forEach(name => {
            if (EVENT_NAMES[name] && saved.fired[name] === true) ledger.fired[name] = true;
          });
        }
      } catch (_) {
        // Storage is an optimisation for restored-state dedupe, never a UI dependency.
      }

      if (!ledger.pageSessionId) {
        const candidate = String(makeUuid());
        // generateUUID() produces this shape. Fail closed if a future generator
        // changes unexpectedly rather than leaking an arbitrary identifier.
        if (UUID_RE.test(candidate)) ledger.pageSessionId = candidate;
      }
    }

    function persist() {
      try {
        storage.setItem(storageKey, JSON.stringify({
          pageSessionId: ledger.pageSessionId,
          fired: ledger.fired,
          savedAt: Date.now()
        }));
      } catch (_) {
        /* analytics must never break the page */
      }
    }

    // Shopify's native Customer Privacy API already encodes whether consent is
    // required in this visitor's region. Missing/throwing API means no event.
    function analyticsAllowed() {
      try {
        const privacy = win.Shopify && win.Shopify.customerPrivacy;
        return !!(privacy &&
          typeof privacy.analyticsProcessingAllowed === 'function' &&
          privacy.analyticsProcessingAllowed() === true);
      } catch (_) {
        return false;
      }
    }

    function locale() {
      try {
        const raw = String((win.document && win.document.documentElement &&
          win.document.documentElement.lang) || '').trim();
        return /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(raw) ? raw.toLowerCase() : 'unknown';
      } catch (_) {
        return 'unknown';
      }
    }

    function dimension(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number < 1 || number > 2000) return null;
      return Math.round(number * 10) / 10;
    }

    function count(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number < 0) return null;
      return Math.min(10000, Math.round(number));
    }

    function emit(eventName, values) {
      if (!EVENT_NAMES[eventName] || !analyticsAllowed()) return false;
      load();
      if (!ledger.pageSessionId || ledger.fired[eventName]) return false;

      const input = values || {};
      const payload = {
        event: eventName,
        source: SOURCE,
        locale: locale(),
        page_session_dedupe_id: ledger.pageSessionId
      };
      const length = dimension(input.drawer_length_mm);
      const width = dimension(input.drawer_width_mm);
      const height = dimension(input.drawer_height_mm);
      const detected = count(input.detected_item_count);
      const placed = count(input.placed_module_count);
      const outcomeAllowed = EVENT_OUTCOMES[eventName] && EVENT_OUTCOMES[eventName][input.outcome];
      const needsDetected = eventName !== 'ai_layout_analysis_started';
      const needsPlaced = eventName === 'ai_layout_generated' || eventName === 'ai_layout_builder_handoff' || eventName === 'ai_layout_lead_submitted';
      if (!outcomeAllowed || length === null || width === null) return false;
      if (needsDetected && detected === null) return false;
      if (needsPlaced && placed === null) return false;
      payload.drawer_length_mm = length;
      payload.drawer_width_mm = width;
      if (height !== null) payload.drawer_height_mm = height;
      if (needsDetected) payload.detected_item_count = detected;
      if (needsPlaced) payload.placed_module_count = placed;
      payload.outcome = input.outcome;

      try {
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push(payload);
        ledger.fired[eventName] = true;
        persist();
        return true;
      } catch (_) {
        return false;
      }
    }

    // Tests use this read-only snapshot to prove restored-state dedupe without
    // exposing the mutable ledger to production callers.
    function snapshot() {
      return JSON.parse(JSON.stringify(ledger));
    }

    return { emit, snapshot };
  }
  // md-ai-layout-events-end

  let aiLayoutAnalytics = null;

  // The page reloaded after the native newsletter form (?customer_posted=true).
  const subscribedOnLoad = (() => {
    try {
      return TEXT_MODE && /(?:^|[?&])customer_posted=true(?:&|$)/.test(window.location.search);
    } catch (_) {
      return false;
    }
  })();

  function analyticsValues(outcome, placedModuleCount) {
    return {
      drawer_length_mm: state.L,
      drawer_width_mm: state.W,
      drawer_height_mm: state.H,
      detected_item_count: outcome === 'started'
        ? undefined
        : (Array.isArray(state.items) ? state.items.length : 0),
      placed_module_count: placedModuleCount,
      outcome
    };
  }

  function placedModuleCountFromHash(hash) {
    const parts = String(hash || '').split('&');
    return parts.filter(part => /^\d+-h=/.test(part)).length;
  }

  // Optional debug flag (can be toggled from console)
  const DEBUG_LAYOUT = typeof window !== 'undefined' && !!window.MD_DEBUG_LAYOUT;

  // Domain knowledge: utensil families and behaviours we prefer to keep contiguous
  const FAMILY_RULES = {
    forks:  ['fork','dinner fork','dessert fork','forks'],
    spoons: ['spoon','teaspoon','table spoon','tablespoon','soup spoon','spoons'],
    knives: ['knife','table knife','steak knife','butter knife','bread knife','knives'],
    // Long cooking tools / utensils
    longTools: ['spatula','ladle','tongs','whisk','turner','peeler','skimmer','pasta server'],
    // Strong hint that this is a sharp kitchen knife
    sharpKnives: ['chef','santoku','cleaver','paring','carving','boning','fillet','utility knife']
  };

  function familyOf(rawName){
    const s = String(rawName||'').toLowerCase();
    for (const [fam, list] of Object.entries(FAMILY_RULES)) {
      if (list.some(k => s.includes(k))) {
        if (fam === 'forks' || fam === 'spoons' || fam === 'knives') {
          return 'cutlery:' + fam;
        }
        if (fam === 'longTools') return 'tool:long';
        if (fam === 'sharpKnives') return 'knife:sharp';
        return fam;
      }
    }
    return '';
  }

  // Richer classifier: combines name + geometry into a semantic family
  function classifyItem(item){
    const baseFam = familyOf(item.name);
    const len = Number(item.approxLenMM || 0);
    const wid = Number(item.approxWidMM || 0);
    const ratio = wid > 0 ? (len / wid) : 0;
    const isLong = len >= 260 || ratio >= 2.0; // long spatulas / knives / tools
    const name = String(item.name || '').toLowerCase();

    const isSharpKnife =
      /chef|santoku|cleaver|boning|fillet|carving|paring|bread knife|utility knife/.test(name);

    if (isSharpKnife) {
      return isLong ? 'knife:sharp_long' : 'knife:sharp';
    }

    if (baseFam === 'cutlery:knives') {
      return isLong ? 'cutlery:knives_long' : baseFam;
    }

    // A named cutlery item (fork/spoon) keeps its family even when it reads as
    // "long & thin" (cutlery is naturally ~3-5x longer than wide). Only items
    // with NO cutlery/tool family fall through to the generic long-tool bucket.
    // Previously `|| isLong` forced forks/spoons to tool:long, collapsing all
    // cutlery onto one oversized generic organiser.
    if (baseFam === 'tool:long') return 'tool:long';
    if (baseFam) return baseFam;
    if (isLong) return 'tool:long';

    // Fallback bucket
    return 'misc';
  }

  function formatDisplayName(raw){
    const s = String(raw || '').trim();
    if (!s) return '';
    // Replace common slug separators with spaces, collapse whitespace
    const cleaned = s.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
    // Title-case for nicer display, but not after an apostrophe ("Chef's", not "Chef'S")
    return cleaned.toLowerCase().replace(/(^|[^\w'’])(\w)/g, (m, before, c) => before + c.toUpperCase());
  }

  // ----- Taxonomy helpers (metaobjects + product metafield references) -----

  // Normalise separators so vision's space-delimited names ("dinner forks")
  // match the taxonomy's hyphenated handles/objectNames ("dinner-forks"). This
  // mismatch silently broke taxonomy matching for most cutlery, sending them to
  // a generic size-only module instead of their proper organiser.
  // md-ai-taxonomy:start — keep matching policy aligned with engine/src/taxonomy.ts.
  function normTaxName(x){ return String(x || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim(); }
  function matchTaxonomyName(name, taxonomy){
    const s = normTaxName(name);
    if (!s) return '';
    let bestTier = 0;
    let bestLength = 0;
    const candidates = new Set();
    const contains = (a, b) => ` ${a.replace(/[(),.;:]/g, ' ')} `.includes(` ${b} `);
    for (const entry of taxonomy) {
      const words = [entry.objectName, ...(entry.aliases || [])].map(normTaxName);
      for (const [i, w] of words.entries()) {
        if (!w) continue;
        const tier = s === w ? (i === 0 ? 4 : 3) : contains(s, w) ? 2 : contains(w, s) ? 1 : 0;
        const length = tier === 2 ? w.length : 0;
        if (!tier) continue;
        if (tier > bestTier || (tier === bestTier && length > bestLength)) { bestTier = tier; bestLength = length; candidates.clear(); }
        if (tier === bestTier && length === bestLength) candidates.add(entry.id);
      }
    }
    return candidates.size === 1 ? [...candidates][0] : '';
  }
  // md-ai-taxonomy:end

  function taxonomyKeyForItem(item){
    // An item that came with its taxonomy id (the describe reader, the item
    // picker) keeps it; a photo item is matched by its name.
    if (item && item.taxonomyId) return String(item.taxonomyId);
    // A described item the reader found no entry for keeps none: matching its name
    // put "steak knives" in the dinner knives' lane (2026-10-07).
    if (item && item.taxonomyRead) return '';
    if (!Array.isArray(state.taxonomy) || !state.taxonomy.length) return '';
    return matchTaxonomyName(item && item.name, state.taxonomy);
  }

  function getTaxonomyById(id){
    if (!id || !Array.isArray(state.taxonomy)) return null;
    return state.taxonomy.find(t => t.id === id) || null;
  }

  function isSoloOnlyItem(item, family, taxId){
    // Sharp knives always solo
    if (family && family.startsWith('knife:sharp')) return true;
    // Check taxonomy's can_be_paired field (default true when missing)
    const t = getTaxonomyById(taxId);
    if (t && t.canBePaired === false) return true;
    // Otherwise allow pairing
    return false;
  }


  function el(tag, props={}, children=[]) {
    const n = document.createElement(tag);
    Object.entries(props || {}).forEach(([k,v]) => {
      if (k === 'class') {
        n.className = v;
      } else if (k === 'style') {
        Object.assign(n.style, v);
      } else if (k.startsWith('on') && typeof v === 'function') {
        n.addEventListener(k.substring(2).toLowerCase(), v);
      } else if (typeof v === 'boolean') {
        // Boolean attributes: only set when true; false means omit.
        if (v) n.setAttribute(k, '');
      } else {
        n.setAttribute(k, v);
      }
    });
    const kids = Array.isArray(children) ? children : (children == null ? [] : [children]);
    // Skip null/undefined/false children so callers can use inline conditionals
    // (e.g. `cond ? el(...) : null`) without appendChild(null) throwing.
    kids.forEach(c => { if (c == null || c === false) return; n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }

  // Horizontal swipe-to-advance over the wizard stages, mirroring the drawer-builder
  // onboarding swipe gesture (axis-locked, rubber-banded at the ends, bails on
  // interactive targets so inputs/buttons/links still work). Re-attached each render
  // to the active card (the previous card is discarded, so no listener leak).
  function setupStageSwipe(cardEl, stepIdx, STEPS, getAvailability, goStep){
    if (!cardEl || !('ontouchstart' in window)) return;
    const nextKey = STEPS[stepIdx + 1], prevKey = STEPS[stepIdx - 1];
    let startX = 0, startY = 0, dragging = false, decided = false, horiz = false, width = 1;
    const onStart = e => {
      const t = e.target;
      if (t && t.closest && t.closest('input,button,select,a,textarea,label,summary,[role=button]')) return;
      const p = e.touches ? e.touches[0] : e;
      startX = p.clientX; startY = p.clientY; dragging = true; decided = false; horiz = false;
      width = cardEl.getBoundingClientRect().width || 1;
      cardEl.style.transition = 'none';
    };
    const onMove = e => {
      if (!dragging) return;
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - startX, dy = p.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        decided = true; horiz = Math.abs(dx) > Math.abs(dy);
        if (!horiz) { dragging = false; return; }  // vertical: let the page scroll
      }
      if (e.cancelable) e.preventDefault();
      let d = dx;
      const forwardBlocked = d < 0 && !(nextKey && getAvailability()[nextKey]);
      const backBlocked = d > 0 && !prevKey;
      if (forwardBlocked || backBlocked) d *= 0.3;  // rubber-band at the ends
      cardEl.style.transform = 'translateX(' + d + 'px)';
    };
    const onEnd = e => {
      cardEl.style.transition = '';
      cardEl.style.transform = '';
      if (!dragging || !horiz) { dragging = false; return; }
      dragging = false;
      const p = (e.changedTouches && e.changedTouches[0]) || e;
      const dx = p.clientX - startX, threshold = width * 0.25;
      if (dx <= -threshold && nextKey && getAvailability()[nextKey]) goStep(nextKey);
      else if (dx >= threshold && prevKey) goStep(prevKey);
    };
    cardEl.addEventListener('touchstart', onStart, { passive: true });
    cardEl.addEventListener('touchmove', onMove, { passive: false });
    cardEl.addEventListener('touchend', onEnd);
    cardEl.addEventListener('touchcancel', () => { dragging = false; cardEl.style.transition = ''; cardEl.style.transform = ''; });
  }

  const state = {
    requestUuid: generateUUID(), // Session UUID for tracking analyze + URL generation
    step: TEXT_MODE ? 'describe' : 'photo', // photo + size → optional description → items
    description: '',   // the visitor's words, kept with the photo or text reading
    descriptionDirty: false, // edited words must be applied before reusing a layout
    storageFailed: false,
    layoutError: false,
    optionSet: null, optionInputs: '', accountSave: '', photoSaveFailed: false, choiceSaveFailed: false,
    photoCollapsed: false, selectedItem: 0,
    photoRead: null, // immutable photo snapshot + in-flight promise
    briefTask: null, // only the current Continue may apply a result
    briefError: '',
    photoAppliedItems: null,
    photoRemovedItems: [],
    reading: null,     // text mode: { text, status: 'reading'|'ok'|'failed', result, error, promise }
    requirements: null, // validated again by the engine request parser
    readingApplied: '', // text mode: the description the current items were read from
    sizeFromText: null, // text mode: the drawer size the description gave, once it filled the fields
    sizeNeeded: false,  // text mode: "Design my layout" found no size, typed or written
    archetype: 'essentialist', // text mode: the design shown (the engine's three)
    lead: { status: 'idle', email: '', marketing: false, error: '' }, // text mode: "Email me this layout"
    imageFile: null,
    imageUrl: '',     // cached object URL for the photo (created once, revoked on replace)
    openRow: null,    // index of the expanded review row in the accordion (null = all collapsed)
    lastStepIdx: null, // last rendered stage index, for the slide-direction transition
    L: 0, W: 0, H: 0,
    taxonomy: [],
    items: [],
    analyzing: false,
    analyzingMsgIdx: 0,
    analyzingTicker: null,
    generatingUrl: false,
    generatingMsgIdx: 0,
    generatingTicker: null,
    optimiser: null,  // { hash, stats, selection, designs } from the layout engine (the Worker's optimise)
    optimising: false, // true while a re-optimise round-trip is in flight (after an edit)
    optimiseDirty: false // true when items changed since the last successful optimise (blocks Generate)
  };

  // Compress an image on the client to stay under Shopify App Proxy limits (~1 MB)
  async function compressImageToDataUrl(file, options){
    const { maxWidth=1280, maxHeight=1280, maxBytes=800*1024, mime='image/jpeg' } = options || {};
    const srcUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise((res, rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=srcUrl; });
      let scale = Math.min(1, maxWidth / (img.naturalWidth||img.width||1), maxHeight / (img.naturalHeight||img.height||1));
      let quality = 0.85;
      let dataUrl = '';
      for (let attempt=0; attempt<10; attempt++){
        const w = Math.max(1, Math.floor((img.naturalWidth||img.width) * scale));
        const h = Math.max(1, Math.floor((img.naturalHeight||img.height) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { alpha: false });
        ctx.drawImage(img, 0, 0, w, h);
        dataUrl = canvas.toDataURL(mime, quality);
        const bytes = Math.floor((dataUrl.length * 3) / 4); // base64 -> bytes approx
        if (bytes <= maxBytes) return { dataUrl, bytes };
        // tighten: first reduce quality, then downscale
        if (quality > 0.5) { quality -= 0.1; }
        else { scale *= 0.8; }
      }
      // return best-effort
      const bytes = Math.floor((dataUrl.length * 3) / 4);
      return { dataUrl, bytes };
    } finally { URL.revokeObjectURL(srcUrl); }
  }

  async function fetchTaxonomy() {
    try {
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', VERSION, 'fetchTaxonomy:start');
      // Routed through the single Shopify App Proxy base: /apps/vision-extract/*
      const r = await fetch('/apps/vision-extract/object-taxonomy');
      if (!r.ok) throw new Error('404 taxonomy');
      const j = await r.json();
      state.taxonomy = j.items || [];
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'fetchTaxonomy:ok', { count: state.taxonomy.length });
    } catch(e) { console.warn('taxonomy fetch failed', e); }
  }

  // Sequence guard: rapid edits fire overlapping /solve/items round-trips; only the
  // LATEST response may update state.optimiser, so a slower older response can't
  // clobber a newer edit. optimiseInFlight keeps the "optimising" state true until
  // every in-flight round-trip has settled. refreshTimer debounces field edits.
  let optimiseSeq = 0;
  let optimiseInFlight = 0;
  let refreshTimer = null;

  // Ask the layout engine (the Worker's optimise) to choose and place modules for
  // the detected items. Stores state.optimiser = { hash, stats, selection } on
  // success, or null when it could not design the drawer. Never throws. Stale
  // responses are dropped.
  async function runOptimiser(opts) {
    if (!state.items || !state.items.length || !state.L || !state.W) {
      state.optimiser = null; state.optimiseDirty = false; return;
    }
    // forDesign is the final Generate pass. Both paths use the same layout engine;
    // the last pass gets a longer relay budget and must use the current edited items.
    const forDesign = !!(opts && opts.forDesign);
    state.optionSet = null; state.accountSave = '';
    saveStateToStorage();
    const seq = ++optimiseSeq;
    const items = state.items.map(it => {
      const taxId = taxonomyKeyForItem(it);
      return {
        name: it.name || '',
        taxonomyId: taxId || null,
        approxLenMM: +it.approxLenMM || 0,
        approxWidMM: +it.approxWidMM || 0,
        qty: Math.max(1, Math.round(it.qty || 1)),
        ...(it.qtyRange ? {qtyRange:it.qtyRange} : {}),
        region: it.region || '',
        ...(it.countConfidence === 'low' || it.countConfidence === 'high' ? { countConfidence: it.countConfidence } : {}),
        // Pass the client's solo signal so the solver isolates solo-only items
        // (e.g. sharp knives) even when taxonomy matching missed (taxId null).
        canBePaired: it.canBePaired === false ? false : !isSoloOnlyItem(it, classifyItem(it), taxId)
      };
    });
    // Hard timeout so a hung Worker can't strand the layout in "Optimising…"
    // forever. On abort the fetch rejects -> caught -> no layout, and
    // refreshLayout's finally settles.
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timeoutId = ctrl ? setTimeout(() => ctrl.abort(), forDesign ? 28000 : 9000) : null;
    try {
      const r = await fetch(VISION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
        body: JSON.stringify({
          action: 'optimise', requestUuid: state.requestUuid, sessionId: currentSessionId,
          drawerLenMM: state.L, drawerWidMM: state.W,
          drawerHeiMM: normaliseDrawerInteriorHeightMm(state.H) || 0,
          items, useDesigner: forDesign, compareOptions: forDesign,
          ...(state.requirements ? {requirements:state.requirements} : {}),
          ...(TEXT_MODE ? { surface: SURFACE, archetype: state.archetype } : {})
        })
      });
      const j = await r.json().catch(() => ({}));
      if (seq !== optimiseSeq) return; // superseded by a newer edit — drop this stale result
      const modules = (j && j.selection && Array.isArray(j.selection.modules)) ? j.selection.modules : [];
      const ok = r.ok && j && j.hash && !j.error
        && (!j.stats || j.stats.solverStatus !== 'ERROR')
        && modules.length > 0; // quality gate: never adopt an empty layout
      if (ok) {
        state.optimiser = { hash: j.hash, stats: j.stats || {}, selection: j.selection, designs: Array.isArray(j.designs) ? j.designs : null };
        if (forDesign && validOptionSet(j.optionSet)) {
          state.optionSet = j.optionSet;
          state.optionInputs = optionInputKey();
          chooseOption(j.optionSet.options[0].id, false);
        }
        window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT] optimiser:ok', j.stats);
      } else {
        console.warn('[AI-LAYOUT] optimiser:unavailable', (j && (j.error || (j.stats && j.stats.solverStatus))) || r.status);
        state.optimiser = null;
      }
      state.optimiseDirty = false;
      saveStateToStorage();
    } catch (e) {
      if (seq === optimiseSeq) { state.optimiser = null; state.optimiseDirty = false; }
      console.warn('[AI-LAYOUT] optimiser:failed', e);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // Re-derive the layout after an edit (qty/size/remove/add), with an
  // "optimising" state. optimising clears only when the LAST in-flight round-trip
  // settles, so overlapping refreshes don't flip it off early or leave a stale
  // result behind.
  async function refreshLayout(){
    optimiseInFlight++;
    state.optimising = true; render();
    // try/finally so the in-flight balance ALWAYS settles — if anything in
    // runOptimiser threw (e.g. a synchronous preamble error), the counter must not
    // strand >0 or "optimising" sticks true and Generate is permanently disabled.
    try {
      await runOptimiser();
    } finally {
      optimiseInFlight = Math.max(0, optimiseInFlight - 1);
      if (optimiseInFlight === 0) state.optimising = false;
      if (state.step !== 'brief') render();
    }
  }

  // Debounced refresh for field edits — coalesces rapid L/W/Qty blurs into one
  // solver round-trip. Marks the layout dirty immediately so Generate is gated.
  function scheduleRefresh(delayMs){
    ++optimiseSeq;
    state.optionSet = null; state.accountSave = '';
    state.optimiseDirty = true;
    saveStateToStorage();
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { refreshTimer = null; refreshLayout(); }, (delayMs == null ? 450 : delayMs));
  }

  // md-describe-drawer-start
  // Pure helpers of the text mode, pinned by tests/describe-drawer-page.test.js.

  // The analyze call for a text part: one reading, one request uuid.
  function describeRequestBody(text, requestUuid, sessionId, locale) {
    return {
      action: 'analyze',
      requestUuid,
      sessionId: sessionId || '',
      locale: ['en', 'nl', 'de', 'fr'].indexOf(locale) >= 0 ? locale : null,
      surface: 'describe-drawer',
      inputs: [{ id: 't1', kind: 'text', text: String(text || '') }]
    };
  }

  // The reader's items in the shape the wizard keeps (the photo reader's, plus
  // the taxonomy id and where each size and count came from).
  function itemsFromReading(result) {
    const items = result && Array.isArray(result.items) ? result.items : [];
    return items
      .filter(it => it && typeof it.name === 'string' && it.name.trim())
      .map(it => ({
        name: it.name.trim(),
        ...(typeof it.label === 'string' && it.label.trim() ? { label: it.label.trim().slice(0, 60) } : {}),
        taxonomyId: typeof it.taxonomyId === 'string' && it.taxonomyId ? it.taxonomyId : null,
        // The reader looked it up: no entry is its answer, not a gap for the name match.
        taxonomyRead: it.taxonomyRead !== false,
        approxLenMM: Math.max(0, +it.approxLenMM || 0),
        approxWidMM: Math.max(0, +it.approxWidMM || 0),
        qty: Math.max(1, Math.round(+it.qty || 1)),
        region: typeof it.region === 'string' ? it.region : '',
        bbox: Array.isArray(it.bbox) && it.bbox.length === 4 ? it.bbox.map(Number) : null,
        countConfidence: it.countConfidence === 'low' ? 'low' : 'high',
        canBePaired: it.canBePaired === false ? false : true,
        sizeSource: typeof it.sizeSource === 'string' ? it.sizeSource : null,
        ...(it.qtyRange && Number.isInteger(it.qtyRange.min) && Number.isInteger(it.qtyRange.max) && it.qtyRange.min > 0 && it.qtyRange.max >= it.qtyRange.min && it.qtyRange.max <= 100 ? {qtyRange:{min:it.qtyRange.min,max:it.qtyRange.max}} : {}),
        qtySource: typeof it.qtySource === 'string' ? it.qtySource : null
      }));
  }

  // Keep the same bounded intent across a reading, edits and a session restore.
  // The server remains authoritative; local storage can supply no private engine fields.
  function requirementsFromReading(result) {
    const given = result && result.requirements;
    const r = given && typeof given === 'object' && !Array.isArray(given) ? given : {};
    const out = {};
    const phrases = result && Array.isArray(result.preferences) ? result.preferences : r.preferences;
    if (Array.isArray(phrases)) {
      const preferences = phrases.filter(p => typeof p === 'string').map(p => p.trim().slice(0, 120)).filter(Boolean).slice(0, 5);
      if (preferences.length) out.preferences = preferences;
    }
    if (['Cotton White','Charcoal Black','Army Blue','Olive Green'].indexOf(r.finish) >= 0) out.finish = r.finish;
    if (r.frontBack === 'long-lanes-back' || r.frontBack === 'cutlery-front') out.frontBack = r.frontBack;
    for (const key of ['separateGroups','matchingCutleryWidths','fullDepth']) if (r[key] === true) out[key] = true;
    const count = r.compartmentCount;
    if (count && Number.isInteger(count.min) && Number.isInteger(count.max) && count.min > 0 && count.max >= count.min && count.max <= 12) out.compartmentCount = {min:count.min,max:count.max};
    return Object.keys(out).length ? out : null;
  }

  // A drawer size the text gave, as the size fields take it; null when it gave none.
  function drawerFromReading(result) {
    const d = result && result.drawer;
    if (!d || !(+d.lengthMM > 0) || !(+d.widthMM > 0)) return null;
    return { L: +d.lengthMM, W: +d.widthMM, H: +d.heightMM > 0 ? +d.heightMM : 0 };
  }

  // The drawer a design uses: the size typed in the fields, else the one the
  // description gave; null when neither has one. A height typed in its field
  // wins. `previous` is the size an earlier reading filled in: fields that still
  // hold it are the text's, so a new reading replaces them (and keeps them when
  // it gives none). `read` is what was taken from the text, for the note beside
  // the layout (null when the fields decided).
  function sizeForDesign(typed, result, previous) {
    const fields = typed || {};
    const filled = !!(previous && +fields.L === previous.L && +fields.W === previous.W);
    if (!filled && +fields.L > 0 && +fields.W > 0) return { L: +fields.L, W: +fields.W, H: fields.H, read: null };
    const size = drawerFromReading(result);
    if (!size) return filled ? { L: +fields.L, W: +fields.W, H: fields.H, read: previous } : null;
    const ownHeight = +fields.H > 0 && !(filled && previous.H > 0 && +fields.H === previous.H);
    const heightRead = size.H > 0 && !ownHeight;
    return { L: size.L, W: size.W, H: heightRead ? size.H : (ownHeight ? fields.H : 0), read: { L: size.L, W: size.W, H: heightRead ? size.H : 0 } };
  }

  // What the page says about a reading under the items: the words it could not
  // place, and whether counts or sizes were assumed.
  function readingNotes(result, items) {
    const notes = [];
    const unrecognised = result && Array.isArray(result.unrecognised)
      ? result.unrecognised.filter(x => typeof x === 'string' && x.trim()).map(x => x.trim()).slice(0, 6)
      : [];
    if (unrecognised.length) notes.push({ key: 'unrecognised', items: unrecognised.join(', ') });
    const list = Array.isArray(items) ? items : [];
    if (list.some(it => it.countConfidence === 'low')) notes.push({ key: 'assumed_counts' });
    if (list.some(it => it.sizeSource && it.sizeSource !== 'stated')) notes.push({ key: 'typical_sizes' });
    return notes;
  }
  // md-describe-drawer-end

  // The page's language, as the describe route takes it.
  function pageLocale() {
    try {
      return String(document.documentElement.lang || '').slice(0, 2).toLowerCase();
    } catch (_) {
      return '';
    }
  }

  // Read the description; resolves { ok, result } or { ok: false, error }, never throws.
  async function postDescribe(text, requestUuid, photoContext) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timeoutId = ctrl ? setTimeout(() => ctrl.abort(), 45000) : null;
    try {
      const r = await fetch(VISION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
        body: JSON.stringify(Object.assign(describeRequestBody(text, requestUuid, currentSessionId, pageLocale()), photoContext ? { surface: 'photo-description', photoContext } : {}))
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || j.ok !== true || !Array.isArray(j.items)) {
        return { ok: false, error: (j && typeof j.error === 'string') ? j.error : 'http_' + r.status };
      }
      // An older Worker may strip photoContext: never accept its text-only replacement.
      if (photoContext && String(j.version || '').indexOf('photo-brief-') !== 0) return { ok: false, error: 'photo_brief_unavailable' };
      return { ok: true, result: j };
    } catch (e) {
      return { ok: false, error: e && e.name === 'AbortError' ? 'timeout' : 'network' };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // Start reading the current description (once per text). Each reading is its
  // own run: a new request uuid, which the optimise calls that follow share.
  function startDescribe() {
    const text = String(state.description || '').trim();
    if (text.length < DESCRIBE_MIN_CHARS) return null;
    if (state.reading && state.reading.text === text && state.reading.status !== 'failed') return state.reading.promise;
    state.requestUuid = generateUUID();
    const reading = { text, status: 'reading', result: null, error: null, promise: null };
    reading.promise = postDescribe(text, state.requestUuid).then(answer => {
      reading.status = answer.ok ? 'ok' : 'failed';
      reading.result = answer.ok ? answer.result : null;
      reading.error = answer.ok ? null : answer.error;
      return reading;
    });
    state.reading = reading;
    return reading.promise;
  }

  // Text mode's height is optional: blank means standard modules.
  function textHeightBlank() {
    return TEXT_MODE && (state.H === 0 || state.H === '' || state.H == null);
  }

  // "Design my layout": read the description, take the size typed in the fields
  // (or, while they are not filled in, the one the text gave), take the items
  // read (unless the visitor already edited the items read from this same text),
  // then lay them out. With no size anywhere it asks for one and stays put.
  async function designFromDescription() {
    if (state.analyzing) return;
    const text = String(state.description || '').trim();
    if (text.length < DESCRIBE_MIN_CHARS) return;
    if (!textHeightBlank() && normaliseDrawerInteriorHeightMm(state.H) == null) return;
    const t0 = Date.now();
    state.analyzing = true;
    state.sizeNeeded = false;
    state.analyzingMsgIdx = 0;
    // The analysis starts once the drawer is known: a typed size now, a written one once read.
    let started = false;
    const emitStarted = () => {
      if (started) return;
      started = true;
      if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_analysis_started', analyticsValues('started'));
    };
    if (state.L > 0 && state.W > 0) emitStarted();
    if (state.analyzingTicker) clearInterval(state.analyzingTicker);
    state.analyzingTicker = setInterval(() => {
      const step = Math.min(AI_DESCRIBE_MSGS.length - 1, Math.floor((Date.now() - t0) / 2600));
      if (step !== state.analyzingMsgIdx) {
        state.analyzingMsgIdx = step;
        setThinkingLine('ai-analyze-thinking', AI_DESCRIBE_MSGS[step]);
      }
    }, 400);
    render();
    try {
      const reading = await (startDescribe() || Promise.resolve(null));
      const read = !!(reading && reading.status === 'ok');
      const size = sizeForDesign({ L: state.L, W: state.W, H: state.H }, read ? reading.result : null, state.sizeFromText);
      if (!size) {
        // The reading is kept: a second click with a size reuses it.
        state.sizeNeeded = true;
        return;
      }
      if (size.read) {
        state.L = size.L;
        state.W = size.W;
        state.H = size.H;
        state.sizeFromText = size.read;
      }
      emitStarted();
      if (read) {
        if (state.readingApplied !== text) {
          state.items = itemsFromReading(reading.result);
          state.requirements = requirementsFromReading(reading.result);
          state.readingApplied = text;
          state.descriptionDirty = false;
          state.openRow = null;
        }
        if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_analysis_completed', analyticsValues('success'));
      } else {
        if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_analysis_completed', analyticsValues('error'));
        state.descriptionDirty = true;
        state.step = 'describe';
        saveStateToStorage();
        return; // keep the reviewed inventory until a retry or explicit manual review
      }
      state.descriptionDirty = false;
      state.step = 'items';
      if (state.items.length) {
        try { await runOptimiser(); } catch (e) { console.warn('[AI-LAYOUT] describe:optimise failed', e); }
      }
      saveStateToStorage();
    } finally {
      if (state.analyzingTicker) { clearInterval(state.analyzingTicker); state.analyzingTicker = null; }
      state.analyzing = false;
      await render();
      if (state.sizeNeeded) {
        const length = document.querySelector('#' + MOUNT_ID + ' .ai-dims input');
        if (length && typeof length.focus === 'function') length.focus();
      }
    }
  }

  // Read the current state for both rendered controls and gestures. Editing or
  // undoing a description must not require replacing the focused textarea.
  function navigationAvailability() {
    const hasItems = !!(state.items && state.items.length);
    return {
      photo: true,
      describe: true,
      options: !!state.optionSet && !state.descriptionDirty && !state.optimiseDirty && !state.optimising,
      brief: !!state.photoRead,
      // A photo brief must be applied before its reviewed inventory is accessible.
      items: !state.descriptionDirty && ((TEXT_MODE ? hasItems : hasItems && state.step !== 'brief' && !state.briefTask) || state.step === 'items')
    };
  }

  // Editing words must not leave an older inventory or layout accessible through
  // the progress bars. Keep the textarea DOM intact while the visitor is typing.
  function updateDescription(value) {
    state.description = String(value || '').slice(0, DESCRIBE_MAX_CHARS);
    const text = state.description.trim();
    const applied = TEXT_MODE ? text : state.photoRead && state.photoRead.requestUuid + ':' + text;
    state.descriptionDirty = !applied || state.readingApplied !== applied;
    state.briefError = '';
    if (state.descriptionDirty) {
      ++optimiseSeq;
      if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
      state.optionSet = null; state.accountSave = ''; state.optimiser = null;
      state.optimiseDirty = true;
    }
    const canGo = navigationAvailability();
    document.querySelectorAll('[data-ai-forward]').forEach(node => { node.disabled = !canGo[node.dataset.aiForward]; });
    saveStateToStorage();
  }

  function resumedStep(savedStep) {
    if (state.descriptionDirty) return TEXT_MODE ? 'describe' : (state.imageFile || state.photoRead ? 'brief' : 'photo');
    if (TEXT_MODE && savedStep === 'describe') return 'describe';
    if (!TEXT_MODE && ['photo', 'brief'].includes(savedStep) && (state.imageFile || state.photoRead)) return savedStep;
    if (state.optionSet && savedStep === 'options') return 'options';
    return state.items.length ? 'items' : TEXT_MODE ? 'describe' : state.photoRead ? 'brief' : 'photo';
  }

  function optionInputKey() {
    return JSON.stringify([state.L, state.W, String(state.H), state.items, state.requirements]);
  }

  function selectedOption() {
    const set = state.optionSet;
    return set && set.options.find(d => d.id === state.archetype) || null;
  }

  async function recordChoice(event, optionId) {
    const set = state.optionSet;
    if (!set || !set.token || !window.mdAiSession) return;
    try { await window.mdAiSession.event(VISION_URL, { token: set.token, event, optionId: optionId || null, eventId: generateUUID() }); }
    catch (_) { state.choiceSaveFailed = true; }
  }

  async function saveAccountOptions() {
    const set = state.optionSet;
    const mount = document.getElementById(MOUNT_ID);
    if (!set || !set.token || mount.getAttribute('data-customer-signed-in') !== 'true' || state.accountSave === 'saving' || state.accountSave === 'saved') return;
    state.accountSave = 'saving';
    try {
      const r = await fetch(VISION_URL, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'save_options', token: set.token }) });
      const body = await r.json();
      if (state.optionSet !== set) return;
      state.accountSave = r.ok && body.ok ? 'saved' : body.error === 'saved_builds_full' ? 'full' : 'failed';
    } catch (_) { if (state.optionSet === set) state.accountSave = 'failed'; }
    if (state.optionSet === set) { saveStateToStorage(); if (state.step === 'options') render(); }
  }

  function chooseOption(id, record) {
    const set = state.optionSet;
    const d = set && set.options.find(o => o.id === id);
    if (!d) return;
    state.archetype = d.id;
    state.optimiser = { hash: d.hash, stats: d.stats, selection: d.selection, designs: set.options };
    state.optimiseDirty = false;
    saveStateToStorage();
    if (record) void recordChoice('selected', d.id);
  }

  function optionBuilderLink(hash) {
    const base = builderHandoffUrl(window.location.origin, mdRoot('pages/drawer-builder'), state.requestUuid, hash);
    const set = state.optionSet, choice = selectedOption();
    if (!set || !choice || choice.hash !== hash) return base;
    const url = new URL(base);
    url.searchParams.set('ai_set', set.id);
    url.searchParams.set('ai_option', choice.id);
    return url.href;
  }

  const WORKSPACE_KEYS = {
    "book_essentialist_name": "modu.builder_js.book_essentialist_name",
    "book_essentialist_best": "modu.builder_js.book_essentialist_best",
    "book_essentialist_tagline": "modu.builder_js.book_essentialist_tagline",
    "book_maximiser_name": "modu.builder_js.book_maximiser_name",
    "book_maximiser_best": "modu.builder_js.book_maximiser_best",
    "book_maximiser_tagline": "modu.builder_js.book_maximiser_tagline",
    "book_organiser_name": "modu.builder_js.book_organiser_name",
    "book_organiser_best": "modu.builder_js.book_organiser_best",
    "book_organiser_tagline": "modu.builder_js.book_organiser_tagline",
    "book_card_facts": "modu.builder_js.book_card_facts",
    "book_design_number": "modu.builder_js.book_design_number",
    "book_modules": "modu.builder_js.book_modules",
    "book_cells": "modu.builder_js.book_cells",
    "book_height": "modu.builder_js.book_height",
    "book_best": "modu.builder_js.book_best",
    "book_items_heading": "modu.builder_js.book_items_heading",
    "book_items_note": "modu.builder_js.book_items_note",
    "book_contents": "modu.builder_js.book_contents",
    "book_code": "modu.builder_js.book_code",
    "book_module": "modu.builder_js.book_module",
    "book_holds": "modu.builder_js.book_holds",
    "book_grid": "modu.builder_js.book_grid",
    "book_compare": "modu.builder_js.book_compare",
    "book_builder_note": "modu.builder_js.book_builder_note",
    "book_edit_description": "modu.builder_js.book_edit_description",
    "book_brief_heading": "modu.builder_js.book_brief_heading",
    "book_brief_note": "modu.builder_js.book_brief_note",
    "workspace_title": "modu.builder_js.workspace_title",
    "workspace_review_title": "modu.builder_js.workspace_review_title",
    "workspace_subtitle": "modu.builder_js.workspace_subtitle",
    "workspace_review_note": "modu.builder_js.workspace_review_note",
    "options_title": "modu.builder_js.options_title",
    "options_intro": "modu.builder_js.options_intro",
    "options_fewer": "modu.builder_js.options_fewer",
    "describe_step": "modu.builder_js.describe_step",
    "describe_hint": "modu.builder_js.describe_hint"
};
  const workspaceT = (key, vars) => mdT(WORKSPACE_KEYS[key], vars);

  function selectInventoryItem(index) {
    state.selectedItem = index;
    document.querySelectorAll('[data-photo-item], [data-inventory-item], [data-item-chip]').forEach(node => {
      const active = Number(node.dataset.photoItem ?? node.dataset.inventoryItem ?? node.dataset.itemChip) === index;
      node.classList.toggle('is-active', active);
      if (node.hasAttribute('aria-pressed')) node.setAttribute('aria-pressed', String(active));
    });
    document.querySelectorAll('[data-module-items]').forEach(node => {
      node.classList.toggle('is-active', (node.dataset.moduleItems || '').split(',').includes(String(index)));
    });
    const caption = document.querySelector('[data-photo-caption]');
    if (caption && state.items[index]) caption.textContent = (index + 1) + '. ' + formatDisplayName(state.items[index].label || state.items[index].name);
  }

  function optionFacts(option) {
    const layout = window.mdLayoutPreview?.parse(option.hash);
    const modules = option.selection.modules;
    const parts = option.hash.split('&');
    const standardHeight = Number((/^mh=([\d.]+)$/.exec(parts.find(part => /^mh=/.test(part)) || '') || [])[1] || 45);
    const heights = layout ? parts.filter(part => /^\d+-h=/.test(part)).map(part => Number((/-hz=([\d.]+)/.exec(part) || [])[1] || standardHeight)) : [];
    return { cells: layout ? layout.modules.reduce((n, m) => n + m.h * m.w, 0) : 0,
      totalCells: layout ? layout.cols * layout.rows : 0,
      height: [...new Set(heights)].sort((a, b) => a - b).join(' / '), count: modules.length };
  }

  function optionCopy(option) {
    const kind = option.id === 'use-case' ? 'organiser' : option.id;
    const count = option.selection.modules.length;
    const number = /^en(?:-|$)/i.test(document.documentElement.lang || 'en') && window.mdQuotePlan ? window.mdQuotePlan.numberWord(count) : count;
    return { name: workspaceT('book_' + kind + '_name'), best: workspaceT('book_' + kind + '_best'),
      tagline: workspaceT('book_' + kind + '_tagline', { count: number }) };
  }

  const sketchCache = new WeakMap();
  function renderQuotePlan(option, small, seed) {
    const layout = window.mdLayoutPreview?.parse(option.hash);
    if (!layout) return null;
    if (!window.mdQuotePlan) return window.mdLayoutPreview.render(document, option.hash, { modules: option.selection.modules });
    let sketch = sketchCache.get(option);
    if (!sketch) {
      const modules = option.selection.modules.map((m, i) => ({ id: 'M' + (i + 1), x0: m.placement.l * 29.5, y0: m.placement.t * 29.5,
        x1: (m.placement.l + m.placement.w) * 29.5, y1: (m.placement.t + m.placement.h) * 29.5 }));
      sketch = window.mdQuotePlan.sketchPlan({ modules }, { widthMM: layout.widthMM, depthMM: layout.lengthMM,
        cols: layout.cols, rows: layout.rows, fitMode: 'standard' }, 1, { labels: true, dims: false, strips: false, fitLabels: true, seed: 60 + seed });
      sketchCache.set(option, sketch);
    }
    const ns = 'http://www.w3.org/2000/svg';
    function svgNode(tag, attrs, text) {
      const node = document.createElementNS(ns, tag);
      Object.entries(attrs || {}).forEach(([key, value]) => node.setAttribute(key, String(value)));
      if (text != null) node.textContent = text;
      return node;
    }
    const svg = svgNode('svg', { class: 'ai-quote-plan' + (small ? ' ai-quote-plan--small' : ''), viewBox: sketch.viewBox.join(' '),
      role: small ? 'img' : 'group', 'aria-label': mdT('modu.builder_js.describe_layout_label', { modules: option.selection.modules.length, size: state.W + ' × ' + state.L + ' mm' }) });
    option.selection.modules.forEach((m, i) => {
      const holds = (m.assignedItems || []).map(a => a.itemIndex), p = m.placement;
      const group = svgNode('g', { class: 'ai-quote-module' + (holds.includes(state.selectedItem) ? ' is-active' : ''), 'data-module-items': holds.join(',') });
      const title = 'M' + (i + 1) + ': ' + m.name + ' · ' + (holds.map(index => formatDisplayName((state.items[index] || {}).label || (state.items[index] || {}).name)).join(', ') || mdT('modu.builder_js.options_spare'));
      group.appendChild(svgNode('title', {}, title));
      group.appendChild(svgNode('rect', { x: p.l * 29.5 + 1, y: p.t * 29.5 + 1, width: p.w * 29.5 - 2, height: p.h * 29.5 - 2 }));
      if (!small && holds.length) {
        group.setAttribute('tabindex', '0'); group.setAttribute('role', 'button'); group.setAttribute('aria-label', title);
        group.addEventListener('click', () => selectInventoryItem(holds[0]));
        group.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectInventoryItem(holds[0]); } });
      }
      svg.appendChild(group);
    });
    const ink = svgNode('g', { class: 'ai-quote-ink', 'aria-hidden': 'true' });
    sketch.paths.forEach(p => ink.appendChild(svgNode('path', { d: `M${p.a[0]},${p.a[1]} Q${p.c[0]},${p.c[1]} ${p.b[0]},${p.b[1]}`, fill: 'none', stroke: p.stroke, 'stroke-width': p.width, opacity: p.opacity, 'stroke-linecap': 'round' })));
    if (!small) sketch.texts.forEach(t => ink.appendChild(svgNode('text', { x: t.x, y: t.y, 'font-family': t.family === 'sans' ? 'Modu Figtree' : 'Modu DM Mono', 'font-size': t.size,
      'font-weight': t.weight, fill: t.fill, 'text-anchor': t.anchor, 'dominant-baseline': 'middle' }, t.text)));
    svg.appendChild(ink);
    return svg;
  }

  function renderOptions() {
    const options = state.optionSet ? state.optionSet.options : [];
    const choice = selectedOption();
    const cards = options.map((d, index) => {
      const selected = d.id === state.archetype, copy = optionCopy(d), facts = optionFacts(d);
      return el('button', { type: 'button', class: 'ai-option' + (selected ? ' is-selected' : ''), 'aria-pressed': String(selected), 'data-option-id': d.id,
        onclick: () => { chooseOption(d.id, true); render(); document.querySelector('[data-option-id="' + d.id + '"]')?.focus({ preventScroll: true }); } }, [
        el('span', { class: 'ai-option__number' }, [String(index + 1).padStart(2, '0')]),
        el('span', { class: 'ai-option__drawing', 'aria-hidden': 'true' }, [renderQuotePlan(d, true, index)]),
        el('span', { class: 'ai-option__copy' }, [el('strong', {}, [copy.name]), el('span', {}, [copy.best]), el('small', {}, [mdT('modu.builder_js.book_card_facts', { modules: facts.count, cells: facts.cells })])]),
        el('span', { class: 'ai-option__check', 'aria-hidden': 'true' }, [selected ? '✓' : ''])
      ]);
    });
    const signedIn = document.getElementById(MOUNT_ID).getAttribute('data-customer-signed-in') === 'true';
    const saveKey = signedIn ? ({ saved: 'modu.builder_js.options_saved', saving: 'modu.builder_js.options_saving', full: 'modu.builder_js.options_full', failed: 'modu.builder_js.options_save_failed' }[state.accountSave] || 'modu.builder_js.options_saving') : 'modu.builder_js.options_local';
    const account = el('div', { class: 'ai-options__save', role: 'status' }, [el('span', {}, [mdT(saveKey)])]);
    if (!signedIn) {
      const login = document.getElementById(MOUNT_ID).getAttribute('data-login-url') || '/account/login';
      account.appendChild(el('a', { href: login + (login.includes('?') ? '&' : '?') + 'return_url=' + encodeURIComponent(window.location.pathname + window.location.search) }, [mdT('modu.builder_js.options_sign_in')]));
    } else if (state.accountSave === 'failed') account.appendChild(el('button', { type: 'button', class: 'ai-text-button', onclick: () => { state.accountSave = ''; void saveAccountOptions(); render(); } }, [mdT('modu.builder_js.options_retry_save')]));
    const copy = choice && optionCopy(choice), facts = choice && optionFacts(choice), index = options.indexOf(choice);
    const chips = state.items.map((it, i) => el('button', { type: 'button', class: 'ai-item-chip' + (i === state.selectedItem ? ' is-active' : ''),
      'data-item-chip': i, 'aria-pressed': String(i === state.selectedItem), onclick: () => selectInventoryItem(i) }, [
      el('span', { class: 'ai-item-chip__number' }, [String(i + 1)]), formatDisplayName(it.label || it.name), el('small', {}, ['×' + it.qty])
    ]));
    const plan = choice ? el('div', { class: 'ai-proposal-workspace' }, [
      el('section', { class: 'ai-proposal', 'aria-label': copy.name }, [
        el('header', { class: 'ai-proposal__title' }, [el('div', {}, [el('p', { class: 'ai-eyebrow' }, [mdT('modu.builder_js.book_design_number', { number: String(index + 1).padStart(2, '0') }), ' · ', mdT('modu.builder_js.options_selected')]), el('h2', {}, [copy.name]), el('p', {}, [copy.tagline])]), el('span', { class: 'ai-proposal__number', 'aria-hidden': 'true' }, [String(index + 1).padStart(2, '0')])]),
        el('div', { class: 'ai-proposal__plan' }, [renderQuotePlan(choice, false, index)]),
        el('p', { class: 'ai-proposal__front' }, [mdT('modu.builder_js.options_front')]),
        el('dl', { class: 'ai-proposal__facts' }, [['book_modules', facts.count], ['book_cells', facts.cells + ' / ' + facts.totalCells], ['book_height', facts.height + ' mm']].map(([key, value]) => el('div', {}, [el('dt', {}, [workspaceT(key)]), el('dd', {}, [String(value)])])))
      ]),
      el('section', { class: 'ai-proposal__items' }, [el('p', { class: 'ai-eyebrow' }, [mdT('modu.builder_js.book_items_heading')]), el('p', { class: 'ai-note' }, [mdT('modu.builder_js.book_items_note')]), el('div', { class: 'ai-item-chips' }, chips),
        el('p', { class: 'ai-note' }, [mdT('modu.builder_js.items_fit_percent', { percent: Math.round(choice.itemsCoveredPct) })])])
    ]) : null;
    const schedule = choice ? el('details', { class: 'ai-option-contents' }, [
      el('summary', {}, [mdT('modu.builder_js.book_contents', { name: copy.name })]),
      el('div', { class: 'ai-table-scroll' }, [el('table', { class: 'ai-module-schedule' }, [
        el('thead', {}, [el('tr', {}, ['book_code', 'book_module', 'book_holds'].map(key => el('th', { scope: 'col' }, [workspaceT(key)])))]),
        el('tbody', {}, choice.selection.modules.map((m, i) => el('tr', { 'data-module-items': m.assignedItems.map(a => a.itemIndex).join(','), class: m.assignedItems.some(a => a.itemIndex === state.selectedItem) ? 'is-active' : '' }, [
          el('th', { scope: 'row' }, ['M' + (i + 1)]), el('td', {}, [m.name, el('small', {}, [mdT('modu.builder_js.book_grid', { length: m.baseH, width: m.baseW })])]),
          el('td', {}, [m.assignedItems.length ? m.assignedItems.map(a => formatDisplayName((state.items[a.itemIndex] || {}).label || a.name) + ' ×' + a.units).join(' · ') : mdT('modu.builder_js.options_spare')])
        ])))
      ])])
    ]) : null;
    const compare = options.length > 1 ? el('details', { class: 'ai-option-contents' }, [el('summary', {}, [mdT('modu.builder_js.book_compare')]), el('div', { class: 'ai-table-scroll' }, [el('table', {}, [
      el('thead', {}, [el('tr', {}, [el('th'), ...options.map(d => el('th', { scope: 'col' }, [optionCopy(d).name]))])]),
      el('tbody', {}, ['book_modules', 'book_cells', 'book_height', 'book_best'].map((key, i) => el('tr', {}, [el('th', { scope: 'row' }, [workspaceT(key)]), ...options.map(d => { const f = optionFacts(d); return el('td', {}, [String([f.count, f.cells + ' / ' + f.totalCells, f.height + ' mm', optionCopy(d).best][i])]); })])))
    ])])]) : null;
    return el('section', { class: 'ai-options', 'aria-label': mdT('modu.builder_js.options_title') }, [
      renderDrawerSummary(true),
      el('div', { class: 'ai-options__grid', role: 'group', 'aria-label': mdT('modu.builder_js.describe_designs_label') }, cards), plan,
      el('div', { class: 'ai-options__details' }, [compare, schedule]),
      el('div', { class: 'ai-options__actions' }, [
        el('div', { class: 'ai-options__edit' }, [el('button', { type: 'button', class: 'ai-btn ai-btn--secondary', onclick: () => { state.step = 'items'; saveStateToStorage(); render(); } }, [mdT('modu.builder_js.options_edit')]), el('p', { class: 'ai-note' }, [mdT('modu.builder_js.book_builder_note')])]),
        el('button', { type: 'button', class: 'ai-btn ai-btn--primary', id: 'ai-open-selected', disabled: !choice || state.generatingUrl, onclick: openInBuilder }, [mdT('modu.builder_js.options_open')])
      ]), account
    ]);
  }

  function renderDrawerSummary(top) {
    const photo = !TEXT_MODE && !!state.imageUrl;
    const body = el('div', { id: 'ai-drawer-photo', class: 'ai-drawer-summary__body', hidden: photo && state.photoCollapsed }, [
      photo ? renderDetectionOverlay() : null,
      !TEXT_MODE && !photo ? el('p', { class: 'ai-note' }, [mdT('modu.builder_js.photo_not_kept')]) : null,
      !TEXT_MODE && !photo ? el('label', { class: 'ai-text-button' }, [mdT('modu.builder_js.choose_photo'), el('input', { type: 'file', accept: 'image/*', class: 'ai-restore-photo', onchange: e => {
        const file = e.target.files && e.target.files[0]; if (!file) return;
        if (state.imageUrl) URL.revokeObjectURL(state.imageUrl);
        state.imageFile = file; state.imageUrl = URL.createObjectURL(file); state.photoCollapsed = false;
        void keepPhoto(file); saveStateToStorage(); render();
      } })]) : null,
      el('div', { class: 'ai-drawer-summary__context' }, [
        el('p', { class: 'ai-drawer-summary__brief' }, [state.description || (TEXT_MODE ? '' : mdT(state.items.some(it => Array.isArray(it.bbox)) ? 'modu.builder_js.photo_select_item' : 'modu.builder_js.photo_ready'))]),
        state.photoSaveFailed ? el('p', { class: 'ai-note', role: 'status' }, [mdT('modu.builder_js.photo_save_failed')]) : null,
        el('button', { type: 'button', class: 'ai-text-button', onclick: () => { state.step = TEXT_MODE ? 'describe' : 'photo'; saveStateToStorage(); render(); } }, [mdT(TEXT_MODE ? 'modu.builder_js.book_edit_description' : 'modu.builder_js.workspace_edit_drawer')])
      ])
    ]);
    const toggle = photo ? el('button', { type: 'button', class: 'ai-photo-toggle', 'aria-expanded': String(!state.photoCollapsed), 'aria-controls': 'ai-drawer-photo', onclick: () => {
      state.photoCollapsed = !state.photoCollapsed; saveStateToStorage(); render(); document.querySelector('.ai-photo-toggle')?.focus({ preventScroll: true });
    } }, [mdT(state.photoCollapsed ? 'modu.builder_js.photo_show' : 'modu.builder_js.photo_hide')]) : null;
    return el('aside', { class: 'ai-drawer-summary' + (top ? ' ai-drawer-summary--top' : '') + (photo && state.photoCollapsed ? ' is-collapsed' : '') }, [
      el('header', { class: 'ai-drawer-summary__header' }, [el('div', {}, [el('strong', {}, [mdT('modu.builder_js.workspace_your_drawer')]), el('span', {'data-drawer-size':'',hidden:!(state.W > 0 && state.L > 0)}, [[state.W, state.L, state.H].filter(v => +v > 0).join(' × ') + ' mm'])]), toggle]), body
    ]);
  }

  function renderDetectionOverlay() {
    if (!state.imageUrl) return null;
    const plane = el('div', { class: 'ai-od__frame' });
    const img = el('img', { class: 'ai-od__img', src: state.imageUrl, alt: mdT('modu.builder_js.drawer_photo_detected_alt'), onload: e => {
      const image = e.target; if (image.naturalWidth && image.naturalHeight) plane.style.maxWidth = 'calc(var(--ai-photo-height) * ' + image.naturalWidth / image.naturalHeight + ')';
    } });
    const boxes = state.items.flatMap((it, idx) => {
      if (!Array.isArray(it.bbox) || it.bbox.length !== 4 || !it.bbox.every(n => Number.isFinite(Number(n)))) return [];
      const x = Math.max(0, Math.min(1, +it.bbox[0])), y = Math.max(0, Math.min(1, +it.bbox[1]));
      const w = Math.max(0, Math.min(1 - x, +it.bbox[2])), h = Math.max(0, Math.min(1 - y, +it.bbox[3]));
      if (!w || !h) return [];
      return [el('button', { type: 'button', class: 'ai-od__box' + (idx === state.selectedItem ? ' is-active' : ''), 'data-photo-item': idx,
        'aria-label': (idx + 1) + '. ' + formatDisplayName(it.label || it.name), 'aria-pressed': String(idx === state.selectedItem),
        style: { left: x * 100 + '%', top: y * 100 + '%', width: w * 100 + '%', height: h * 100 + '%' }, onclick: () => selectInventoryItem(idx) }, [el('span', { class: 'ai-od__tag' }, [String(idx + 1)])])];
    });
    plane.append(img, el('div', { class: 'ai-od__layer' }, boxes));
    return el('div', { class: 'ai-photo-vision' + (state.photoRead?.status === 'reading' ? ' is-reading' : '') }, [plane,
      boxes.length ? el('p', { class: 'ai-photo-caption', 'data-photo-caption': '' }, [state.items[state.selectedItem] ? (state.selectedItem + 1) + '. ' + formatDisplayName(state.items[state.selectedItem].label || state.items[state.selectedItem].name) : mdT('modu.builder_js.photo_select_item')]) : null
    ]);
  }


  // "Email me this layout": on when the section's email setting is (the Klaviyo flow is live).
  const EMAIL_LAYOUT = (() => {
    try {
      const mount = typeof document !== 'undefined' ? document.getElementById(MOUNT_ID) : null;
      return !!(mount && mount.getAttribute('data-email-layout') === 'true');
    } catch (_) {
      return false;
    }
  })();

  // The layout on screen as the builder opens it, or '' when there is none yet.
  function currentHandoffHash() {
    if (!state.optimiser || !state.optimiser.hash) return '';
    const height = textHeightBlank() ? STANDARD_DRAWER_HEIGHT_MM : normaliseDrawerInteriorHeightMm(state.H);
    return reconcileBuilderHashHeight(state.optimiser.hash, height) || '';
  }

  // The customer's own link back to this layout: it names the run, as the hand-off does.
  function builderLink(hash) {
    return optionBuilderLink(hash);
  }

  let customDesignRetryContext = null;
  function syncCustomDesignForm() {
    const section = document.querySelector('[data-ai-custom-design]');
    const form = section && section.querySelector('form');
    if (!form) return;
    const storageKey = `modu-custom-design-${currentSessionId}`;
    if (!form.dataset.designBound) {
      form.dataset.designBound = 'true';
      // Keep the original context through a native validation/captcha return.
      // Contact details stay in Shopify's form, never in this storage record.
      try {
        if (form.querySelector('[data-ai-design-response="success"]')) sessionStorage.removeItem(storageKey);
        else if (form.querySelector('[data-ai-design-response="error"]')) {
          const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
          if (saved && saved.path === window.location.pathname && Date.now() - saved.at < 2 * 60 * 60 * 1000) customDesignRetryContext = saved.context;
        }
      } catch (_) {}
      const details = form.querySelector('details');
      if (details) details.addEventListener('toggle', syncCustomDesignForm);
      form.addEventListener('submit', () => {
        syncCustomDesignForm();
        saveStateToStorage();
        const context = {};
        form.querySelectorAll('[data-ai-design-context]').forEach(field => { context[field.dataset.aiDesignContext] = field.value; });
        try { sessionStorage.setItem(storageKey, JSON.stringify({ path: window.location.pathname, at: Date.now(), context })); } catch (_) {}
        // Let Shopify submit normally, including its native spam protection.
      });
    }
    const hash = currentHandoffHash();
    const current = customDesignContext(state, hash ? builderLink(hash) : '');
    const sameInput = customDesignRetryContext && current.run === customDesignRetryContext.run && current.inventory === customDesignRetryContext.inventory && current.dimensions === customDesignRetryContext.dimensions && current.description === customDesignRetryContext.description && current.option === customDesignRetryContext.option;
    const context = sameInput && !state.optimiser && !state.optimiseDirty && !state.analyzing
      ? customDesignRetryContext : current;
    if (!sameInput) customDesignRetryContext = null;
    if (state.optimiser) customDesignRetryContext = null;
    form.querySelectorAll('[data-ai-design-context]').forEach(field => { field.value = String(context[field.dataset.aiDesignContext] || ''); });
  }

  async function copyLayoutLink() {
    const hash = currentHandoffHash();
    if (!hash) return;
    const url = builderLink(hash);
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {
      const field = el('input', { value: url, readonly: true, style: { position: 'fixed', opacity: '0' } });
      document.body.appendChild(field);
      field.select();
      try { document.execCommand('copy'); } catch (_) {}
      field.remove();
    }
    state.lead.copied = true;
    render();
  }

  // Send the layout by email (the Worker's lead action), then, when the visitor
  // ticked the box, sign them up through the page's native Shopify customer form.
  async function sendLayoutEmail() {
    if (state.lead.status === 'sending') return;
    const email = String(state.lead.email || '').trim();
    const hash = currentHandoffHash();
    if (!hash) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      state.lead.status = 'invalid';
      render();
      return;
    }
    state.lead.status = 'sending';
    render();
    let answer = null;
    try {
      const r = await fetch(VISION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'lead', email, hash, archetype: state.archetype, locale: pageLocale(), surface: SURFACE, requestUuid: state.requestUuid, sessionId: currentSessionId, ...(selectedOption() ? { optionSetId: state.optionSet.id, optionId: selectedOption().id } : {}) })
      });
      answer = await r.json().catch(() => null);
      state.lead.status = r.ok && answer && answer.ok ? 'sent' : (answer && answer.error === 'invalid_email' ? 'invalid' : 'failed');
    } catch (_) {
      state.lead.status = 'failed';
    }
    if (state.lead.status === 'sent' && aiLayoutAnalytics) {
      aiLayoutAnalytics.emit('ai_layout_lead_submitted', analyticsValues('success', placedModuleCountFromHash(hash)));
    }
    if (state.lead.marketing && state.lead.status !== 'invalid') {
      const form = document.getElementById('DescribeDrawerNewsletter');
      const field = form && form.querySelector('[name="contact[email]"]');
      if (form && field) {
        // The page reloads on the native form's answer; the layout comes back from storage.
        saveStateToStorage();
        field.value = email;
        form.submit();
        return;
      }
    }
    render();
  }

  function invalidatePhoto() {
    if (TEXT_MODE) return;
    if (state.photoRead && state.photoRead.controller) state.photoRead.controller.abort();
    state.photoRead = null;
    state.optionSet = null; state.accountSave = '';
    state.briefTask = null;
    state.briefError = '';
    state.analyzing = false;
    state.items = [];
    state.photoAppliedItems = null;
    state.photoRemovedItems = [];
    state.readingApplied = '';
    state.descriptionDirty = !!state.description.trim();
    state.requirements = null;
    state.optimiser = null;
    state.optimiseDirty = true;
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    ++optimiseSeq;
    saveStateToStorage();
  }

  // Update only the status: completing a photo read must never recreate a focused textarea.
  const PHOTO_ACTIVITY_KEYS = {
    photo_brief_failed: 'modu.builder_js.photo_brief_failed',
    photo_designing: 'modu.builder_js.photo_designing',
    photo_considering: 'modu.builder_js.photo_considering',
    photo_reading: 'modu.builder_js.photo_reading',
    photo_ready: 'modu.builder_js.photo_ready',
    photo_read_failed: 'modu.builder_js.photo_read_failed',
  };
  function updatePhotoActivity() {
    const node = document.getElementById('ai-photo-activity');
    if (!node) return;
    const reading = state.photoRead;
    const busy = !!state.briefTask || !!(reading && reading.status === 'reading');
    const key = state.briefError ? 'photo_brief_failed'
      : state.briefTask ? (state.briefTask.stage === 'design' ? 'photo_designing' : state.briefTask.stage === 'brief' ? 'photo_considering' : 'photo_reading')
      : reading && reading.status === 'ok' ? 'photo_ready'
      : reading && reading.status === 'failed' ? 'photo_read_failed' : 'photo_reading';
    node.classList.toggle('is-busy', busy);
    document.querySelectorAll('.ai-photo-vision').forEach(photo => photo.classList.toggle('is-reading', !!reading && reading.status === 'reading'));
    const line = node.querySelector('[data-photo-status]');
    if (line) line.textContent = mdT(PHOTO_ACTIVITY_KEYS[key]);
    const area = document.getElementById('ai-photo-description');
    if (area) area.disabled = !!state.briefTask;
    const next = document.getElementById('ai-photo-continue');
    if (next) next.disabled = !!state.briefTask;
    const manual = document.getElementById('ai-photo-manual');
    if (manual) manual.hidden = busy || !(state.briefError || (reading && reading.status === 'failed'));
  }

  // One background read per photo/dimension snapshot. Never writes the review inventory.
  function startPhotoRead() {
    if (state.photoRead && state.photoRead.status !== 'failed') return state.photoRead;
    const height = normaliseDrawerInteriorHeightMm(state.H);
    if (!state.imageFile || !(state.L > 0 && state.W > 0) || height == null) return null;
    const file = state.imageFile;
    const reading = { status: 'reading', requestUuid: generateUUID(), result: null, controller: new AbortController(), promise: null };
    state.photoRead = reading;
    state.analyzing = true;
    if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_analysis_started', analyticsValues('started'));
    const common = { action: 'analyze', requestUuid: reading.requestUuid, sessionId: currentSessionId,
      drawerLenMM: state.L, drawerWidMM: state.W, drawerHeiMM: height,
      taxonomyHints: (state.taxonomy || []).map(t => ({ name: t.objectName || '', aliases: Array.isArray(t.aliases) ? t.aliases : [] })) };
    reading.promise = (async () => {
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; reading.controller.abort(); }, ANALYZE_TIMEOUT_MS);
      async function send(options) {
        const image = await compressImageToDataUrl(file, options);
        if (reading.controller.signal.aborted) throw new Error('cancelled');
        const response = await fetch(VISION_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          signal: reading.controller.signal, body: JSON.stringify(Object.assign({}, common, { imageBase64: image.dataUrl.split(',')[1] })) });
        if (!response.ok) { const error = new Error('photo HTTP ' + response.status); error.status = response.status; throw error; }
        return response.json(); // deadline includes the body, not just the headers
      }
      try {
        let result;
        try { result = await send({ maxWidth: 1400, maxHeight: 1400, maxBytes: 800*1024 }); }
        catch (error) {
          if (reading.controller.signal.aborted || (error.status && error.status !== 413)) throw error;
          result = await send({ maxWidth: 900, maxHeight: 900, maxBytes: 320*1024 });
        }
        if (!result || !Array.isArray(result.items) || !result.items.length) throw new Error('no items');
        // Keep the legacy photo review normalization, including a zero/unknown count.
        result.items = result.items.map(x => ({ name: x.name || '', approxLenMM:+x.approxLenMM||0, approxWidMM:+x.approxWidMM||0, qty:+x.qty||1, region:String(x.region || '').toLowerCase().trim(), bbox:Array.isArray(x.bbox) && x.bbox.length === 4 ? x.bbox.map(Number) : null, countConfidence:x.countConfidence === 'low' ? 'low' : 'high' }));
        reading.result = result;
        reading.status = 'ok';
      } catch (_) { reading.status = 'failed'; reading.error = timedOut ? 'timeout' : 'failed'; }
      finally {
        clearTimeout(timer);
        if (state.photoRead === reading) {
          state.analyzing = false;
          if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_analysis_completed', analyticsValues(reading.status === 'ok' ? 'success' : 'error'));
          updatePhotoActivity();
          saveStateToStorage();
        }
      }
      return reading;
    })();
    return reading;
  }

  function analyze() {
    const reading = startPhotoRead();
    if (!reading) return;
    state.step = 'brief';
    state.briefError = '';
    saveStateToStorage();
    render();
  }

  // Keep edits made in the review table when a visitor revises the description.
  function keepReviewEdits(next) {
    if (!state.photoAppliedItems) return next;
    const before = state.photoAppliedItems, current = state.items;
    const all = [...before, ...current, ...next, ...(state.photoRemovedItems || [])];
    const names = new Map(all.map(item => [item, normTaxName(item.name).replace(/\s/g, '')]));
    const ids = new Map(all.map(item => [item, taxonomyKeyForItem(item)]));
    const nameKey = item => names.has(item) ? names.get(item) : normTaxName(item.name).replace(/\s/g, '');
    const peersByName = new Map();
    for (const item of all) {
      const id = ids.get(item), name = nameKey(item);
      if (id) { if (!peersByName.has(name)) peersByName.set(name, new Set()); peersByName.get(name).add(id); }
    }
    // Names may change from "dinner-forks" to "Dinner Forks" or an alias when
    // the description is re-read. This is row identity, not new classification.
    const key = item => {
      const id = ids.has(item) ? ids.get(item) : taxonomyKeyForItem(item);
      if (id) return 'tax:' + id;
      const peers = peersByName.get(nameKey(item)) || new Set();
      return peers.size === 1 ? 'tax:' + [...peers][0] : 'name:' + nameKey(item);
    };
    const group = (items, id) => items.filter(item => key(item) === id);
    // Keep explicit removals even if one reader also omitted the item. A manual
    // re-add clears its removal, using the same identity as the review rows.
    const removed = new Map([...(state.photoRemovedItems || []), ...before.filter(item => !current.some(row => key(row) === key(item)))].map(item => [key(item), item]));
    current.forEach(item => removed.delete(key(item)));
    state.photoRemovedItems = [...removed.values()].map(item => ({ name: item.name, taxonomyId: item.taxonomyId, taxonomyRead: item.taxonomyRead }));
    // Clone targets so the raw reader baseline never receives reviewed values.
    const result = next.filter(item => !removed.has(key(item))).map(item => Object.assign({}, item));
    const fields = ['qty','approxLenMM','approxWidMM','countConfidence','qtyRange','sizeSource','qtySource'];
    for (const id of new Set(current.map(key))) {
      const old = group(before, id), now = group(current, id), targets = group(result, id);
      if (!old.length) {
        // Explicit manual additions remain authoritative, without adding them twice.
        for (let i = result.length - 1; i >= 0; i--) if (key(result[i]) === id) result.splice(i, 1);
        result.push(...now.map(item => Object.assign({}, item, { reviewedFields: fields.slice() })));
      } else if (targets.length) {
        if (old.length === 1 && now.length === 1 && targets.length === 1) {
          const reviewed = new Set((Array.isArray(now[0].reviewedFields) ? now[0].reviewedFields : []).filter(field => fields.includes(field)));
          for (const field of fields) if (reviewed.has(field) || JSON.stringify(now[0][field]) !== JSON.stringify(old[0][field])) {
            targets[0][field] = now[0][field]; reviewed.add(field);
          }
          if (reviewed.size) targets[0].reviewedFields = [...reviewed];
        } else {
          // A repeated type can represent several physical piles. If one was
          // edited/removed, retain that reviewed group instead of guessing which
          // new row is the same pile or multiplying a single corrected count.
          const values = items => JSON.stringify(items.map(item => fields.map(field => item[field])));
          if (values(old) !== values(now) || now.some(item => item.reviewedFields && item.reviewedFields.length)) {
            for (let i = result.length - 1; i >= 0; i--) if (key(result[i]) === id) result.splice(i, 1);
            result.push(...now.map(item => Object.assign({}, item, { reviewedFields: fields.slice() })));
          }
        }
      } // No target: an explicit removal in the revised description wins.
    }
    return result;
  }

  async function continuePhoto() {
    if (state.briefTask) return;
    const reading = startPhotoRead();
    if (!reading) return;
    const text = String(state.description || '').trim();
    const task = { stage: 'photo' };
    state.briefTask = task;
    state.briefError = '';
    updatePhotoActivity();
    const current = () => state.briefTask === task && state.photoRead === reading && state.step === 'brief';
    try {
      await reading.promise;
      if (!current()) return;
      if (reading.status !== 'ok') return;
      const applied = reading.requestUuid + ':' + text;
      if (state.readingApplied !== applied) {
        let result = reading.result;
        let runId = reading.requestUuid;
        if (text) {
          task.stage = 'brief'; updatePhotoActivity();
          runId = generateUUID();
          const answer = await postDescribe(text, runId, { requestUuid: reading.requestUuid, items: reading.result.items });
          if (!current()) return;
          if (!answer.ok) { state.briefError = answer.error; return; }
          result = answer.result;
        }
        const rawItems = text ? itemsFromReading(result) : result.items.map(it => Object.assign({}, it));
        state.items = keepReviewEdits(rawItems);
        // Compare future edits against the unmodified reader output. Promoting
        // merged review values here would lose overrides on the next reread.
        state.photoAppliedItems = JSON.parse(JSON.stringify(rawItems));
        state.requirements = requirementsFromReading(result);
        state.requestUuid = runId;
        state.readingApplied = applied;
        state.descriptionDirty = false;
        state.optimiser = null;
        state.optimiseDirty = true;
        state.openRow = null;
      }
      state.descriptionDirty = false;
      task.stage = 'design'; updatePhotoActivity();
      if (state.items.length) await runOptimiser();
      if (!current()) return;
      state.step = 'items';
      saveStateToStorage();
      render();
    } catch (_) {
      if (current()) { state.step = 'items'; state.optimiseDirty = true; render(); }
    } finally {
      if (state.briefTask === task) { state.briefTask = null; updatePhotoActivity(); syncCustomDesignForm(); }
    }
  }

  function reviewPhotoManually() {
    if (state.briefTask) return;
    const reading = state.photoRead;
    if (!state.items.length && reading && reading.status === 'ok') {
      state.items = reading.result.items.map(it => Object.assign({}, it));
      state.photoAppliedItems = JSON.parse(JSON.stringify(reading.result.items));
      state.requestUuid = reading.requestUuid;
    }
    state.step = 'items';
    state.descriptionDirty = false; // explicit fallback: review the photo manually
    state.optimiseDirty = true;
    saveStateToStorage();
    render();
    if (state.items.length) refreshLayout();
  }

  // ============================================================================
  // State Persistence - Save/Restore for back button support (session-aware)
  // ============================================================================
  const STORAGE_KEY_PREFIX = TEXT_MODE ? 'describe-drawer-state' : 'ai-layout-creator-state';
  
  /**
   * Get session-specific storage key
   */
  function getStorageKey() {
    return currentSessionId 
      ? `${STORAGE_KEY_PREFIX}-${currentSessionId}` 
      : STORAGE_KEY_PREFIX;
  }

  function saveStateToStorage() {
    try {
      const storageKey = getStorageKey();
      const toSave = {
        L: state.L,
        W: state.W,
        H: normaliseDrawerInteriorHeightMm(state.H) || 0,
        step: state.step,
        photoCollapsed: state.photoCollapsed, selectedItem: state.selectedItem,
        optionSet: !state.optimiseDirty ? state.optionSet : null,
        optionInputs: state.optionInputs,
        optimiser: !state.optimiseDirty ? state.optimiser : null,
        items: state.items,
        requestUuid: state.requestUuid,
        description: state.description, descriptionDirty: state.descriptionDirty, readingApplied: state.readingApplied, archetype: state.archetype, requirements: state.requirements,
        ...(!TEXT_MODE && state.photoRead && state.photoRead.status === 'ok' ? { photoRead: { requestUuid: state.photoRead.requestUuid, result: state.photoRead.result }, photoAppliedItems: state.photoAppliedItems, photoRemovedItems: state.photoRemovedItems } : {}),
        savedAt: Date.now(),
        sessionId: currentSessionId
      };
      localStorage.setItem(storageKey, JSON.stringify(toSave));
      state.storageFailed = false;
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'saveStateToStorage:ok', { 
        sessionId: getShortSessionId(currentSessionId),
        items: state.items.length 
      });
    } catch (e) {
      state.storageFailed = true;
      console.warn('[AI-LAYOUT]', 'saveStateToStorage:error', e);
    } finally {
      const warning = document.getElementById('ai-storage-warning');
      if (warning) warning.hidden = !state.storageFailed;
    }
  }

  function validOptionSet(set) {
    return !!(set && AI_REQUEST_ID_RE.test(set.id || '') && set.requestUuid === state.requestUuid && typeof set.token === 'string'
      && Array.isArray(set.options) && set.options.length > 0 && set.options.length <= 3 && set.options.every(d =>
        d && ['essentialist', 'maximiser', 'use-case'].includes(d.id) && typeof d.hash === 'string' && d.hash.length < 50000
        && d.stats && d.selection && Array.isArray(d.selection.modules) && d.selection.modules.length > 0));
  }

  async function keepPhoto(file) {
    if (!window.mdAiSession) { state.photoSaveFailed = true; return; }
    const key = getStorageKey();
    try { await window.mdAiSession.photo(key, file); if (state.imageFile === file) state.photoSaveFailed = false; }
    catch (_) { if (state.imageFile === file) { state.photoSaveFailed = true; render(); } }
  }

  async function restoreStateFromStorage() {
    try {
      const storageKey = getStorageKey();
      const saved = localStorage.getItem(storageKey);
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      // Keep this device’s drawer for 30 days, including Back from the builder.
      if (parsed.savedAt && (Date.now() - parsed.savedAt) < 30 * 86400000) {
        state.photoCollapsed = parsed.photoCollapsed === true;
        state.selectedItem = Number.isInteger(parsed.selectedItem) && parsed.selectedItem >= 0 ? parsed.selectedItem : 0;
        state.L = parsed.L || 0;
        state.W = parsed.W || 0;
        state.H = normaliseDrawerInteriorHeightMm(parsed.H) || 0;
        state.items = Array.isArray(parsed.items) ? parsed.items.slice(0, 60) : [];
        if (AI_REQUEST_ID_RE.test(String(parsed.requestUuid || ''))) state.requestUuid = parsed.requestUuid;
        state.description = typeof parsed.description === 'string' ? parsed.description.slice(0, DESCRIBE_MAX_CHARS) : '';
        state.requirements = requirementsFromReading(parsed);
        state.readingApplied = typeof parsed.readingApplied === 'string' ? parsed.readingApplied : '';
        state.descriptionDirty = parsed.descriptionDirty === true;
        state.archetype = ['essentialist','maximiser','use-case'].includes(parsed.archetype) ? parsed.archetype : 'essentialist';
        if (validOptionSet(parsed.optionSet)) {
          state.optionSet = parsed.optionSet;
          state.optionInputs = parsed.optionInputs || '';
          const choice = state.optionSet.options.find(d => d.id === state.archetype) || state.optionSet.options[0];
          state.archetype = choice.id;
          state.optimiser = { hash: choice.hash, stats: choice.stats, selection: choice.selection, designs: state.optionSet.options };
        }
        if (!TEXT_MODE && window.mdAiSession) {
          try { state.imageFile = await window.mdAiSession.photo(storageKey); if (state.imageFile) state.imageUrl = URL.createObjectURL(state.imageFile); }
          catch (_) { state.photoSaveFailed = true; }
        }
        if (!TEXT_MODE && parsed.photoRead && AI_REQUEST_ID_RE.test(String(parsed.photoRead.requestUuid || '')) && parsed.photoRead.result && Array.isArray(parsed.photoRead.result.items)) {
          state.photoRead = Object.assign({}, parsed.photoRead, { status: 'ok', promise: Promise.resolve() });
          state.photoAppliedItems = Array.isArray(parsed.photoAppliedItems) ? parsed.photoAppliedItems : null;
          state.photoRemovedItems = Array.isArray(parsed.photoRemovedItems) ? parsed.photoRemovedItems.filter(item => item && typeof item.name === 'string').slice(0, 60) : [];
        }
        if (TEXT_MODE) {
          state.description = typeof parsed.description === 'string' ? parsed.description.slice(0, DESCRIBE_MAX_CHARS) : '';
          state.requirements = requirementsFromReading(parsed);
          state.readingApplied = typeof parsed.readingApplied === 'string' ? parsed.readingApplied : '';
          if (!state.optionSet) state.archetype = ['essentialist', 'maximiser', 'use-case'].indexOf(parsed.archetype) >= 0 ? parsed.archetype : 'essentialist';
          // Text mode keeps a blank height blank.
          if (!(+parsed.H > 0)) state.H = 0;
        }
        // Migrate older edited descriptions without invalidating unchanged saved choices.
        const expected = TEXT_MODE ? state.description.trim() : state.photoRead && state.photoRead.requestUuid + ':' + state.description.trim();
        if (parsed.descriptionDirty == null && state.readingApplied && expected && state.readingApplied !== expected) state.descriptionDirty = true;
        if (state.descriptionDirty) { state.optionSet = null; state.optimiser = null; state.optimiseDirty = true; }
        state.step = resumedStep(parsed.step);
        window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'restoreStateFromStorage:ok', { 
          sessionId: getShortSessionId(currentSessionId),
          items: state.items.length 
        });
        return true;
      } else {
        // Expired session data - clean it up
        localStorage.removeItem(storageKey);
        window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'restoreStateFromStorage:expired', { 
          sessionId: getShortSessionId(currentSessionId)
        });
      }
    } catch (e) {
      console.warn('[AI-LAYOUT]', 'restoreStateFromStorage:error', e);
    }
    return false;
  }

  function clearSavedState() {
    try {
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      if (window.mdAiSession) void window.mdAiSession.photo(storageKey, null).catch(() => {});
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'clearSavedState:ok', { 
        sessionId: getShortSessionId(currentSessionId)
      });
    } catch (e) {}
  }











  // ============================================================================
  // URL Generation: the engine's layout, opened in the Drawer Builder
  // ============================================================================
  async function generateOptions() {
    if (state.generatingUrl || state.descriptionDirty || state.optimising) return; // never generate from an unapplied description
    // Text mode: a blank height is a standard drawer (it is optional there).
    const interiorHeightMm = textHeightBlank() ? STANDARD_DRAWER_HEIGHT_MM : normaliseDrawerInteriorHeightMm(state.H);
    const targetHeightMm = normaliseDrawerHeightMm(interiorHeightMm);
    if (!state.L || !state.W || interiorHeightMm == null || targetHeightMm == null) {
      alert(mdT('modu.builder_js.invalid_drawer_dimensions'));
      return;
    }
    if (!textHeightBlank()) state.H = interiorHeightMm;
    if (!state.items || !state.items.length) {
      alert(mdT('modu.builder_js.generate_layout_first'));
      return;
    }

    // Lay the CURRENT items out once more and open that layout. Generate is
    // already gated on optimiseDirty/optimising, but this pass is the hard
    // guarantee against redirecting to a stale hash.
    state.layoutError = false;
    state.generatingUrl = true; state.generatingMsgIdx = 0; render();
    // Animate the "designing" overlay through the wait.
    const dt0 = Date.now();
    if (state.generatingTicker) clearInterval(state.generatingTicker);
    state.generatingTicker = setInterval(() => {
      const step = Math.min(AI_GENERATE_MSGS.length - 1, Math.floor((Date.now() - dt0) / 1600));
      if (step !== state.generatingMsgIdx) { state.generatingMsgIdx = step; setThinkingLine('ai-generate-thinking', AI_GENERATE_MSGS[step]); }
    }, 250);
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    await runOptimiser({ forDesign: true });
    if (state.generatingTicker) { clearInterval(state.generatingTicker); state.generatingTicker = null; }
    state.generatingUrl = false;
    if (!state.optionSet) {
      state.layoutError = true;
      render();
      document.getElementById('ai-layout-error')?.scrollIntoView({block:'center'});
      return;
    }
    state.step = 'options';
    saveStateToStorage();
    render();
    void recordChoice('shown');
    void saveAccountOptions();
    document.getElementById('ai-options-title')?.focus({ preventScroll: true });
    document.getElementById(MOUNT_ID)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_generated', analyticsValues('success', state.optimiser.stats.moduleCount));
  }

  async function openInBuilder() {
    if (state.generatingUrl || state.optimising || state.optimiseDirty || state.descriptionDirty) return;
    const choice = selectedOption();
    if (!choice) return generateOptions();
    const handoffHash = currentHandoffHash();
    if (!handoffHash) return;
    state.generatingUrl = true;
    saveStateToStorage();
    render();
    // Queue the explicit handoff before leaving; never re-solve the selected option.
    await recordChoice('selected', choice.id);
    await recordChoice('opened', choice.id);
    if (aiLayoutAnalytics) aiLayoutAnalytics.emit('ai_layout_builder_handoff', analyticsValues('success', choice.moduleCount));
    window.location.href = optionBuilderLink(handoffHash);
  }

  // "What we detected": overlay the vision bounding boxes on the user's photo so
  // they can see HOW the AI read the drawer. Renders only when the backend
  // returned per-item bbox data (graceful: older backend -> no overlay).
  function renderItemsTable() {
    const baseItems = state.items || [];
    // The engine's layout, or null while there is none (not run yet, or it could
    // not design the drawer).
    const hasLayout = !!state.optimiser;

    // Each detected item is its own row, and its module is the tray the engine
    // ASSIGNED it to. With a settled layout the rows are grouped under their
    // tray, so the items that share a module read as one group.
    const itemTray = {}; // raw itemIndex -> { key, name, baseH, baseW, handle, variantId }
    const trayOrder = []; // the trays that hold items, in the engine's order
    if (hasLayout) {
      (state.optimiser.selection.modules || []).forEach((m, mi) => {
        // One group per tray, not per product: three of the same 7x2 are three groups.
        const key = String(m.instanceId || ('m' + mi));
        let holds = false;
        for (const ai of (m.assignedItems || [])) {
          if (ai && typeof ai.itemIndex === 'number' && itemTray[ai.itemIndex] == null) {
            itemTray[ai.itemIndex] = { key, name: m.name, baseH: m.baseH, baseW: m.baseW, handle: m.handle || '', variantId: m.variantId || '' };
            holds = true;
          }
        }
        if (holds) trayOrder.push(key);
      });
    }
    // "Busy" = a re-optimise is pending (debounce) or in flight. While busy, the
    // current state.optimiser still holds the PRE-edit result, so we hide the stale
    // stats + the partial-fill banner and just show "Optimising…".
    const busy = state.optimising || state.optimiseDirty;
    const grouped = false;
    const groups = baseItems.map((it, idx) => ({ name: it.name, approxLenMM: it.approxLenMM || 0, approxWidMM: it.approxWidMM || 0, qty: it.qty || 1, members: [idx] }));

    // Clamp the expanded-row index when the row list shrank, so a stale index
    // doesn't open the wrong row or silently collapse the panel.
    if (state.openRow != null && state.openRow >= groups.length) state.openRow = null;

    // Commit an edited dimension/qty to the row and its item, then lay the
    // drawer out again: mark dirty + render now (gates Generate immediately), and
    // debounce the round-trip so rapid edits don't fire one solve per field.
    function commitField(g, key, value){
      g[key] = value;
      (Array.isArray(g.members) ? g.members : []).forEach(idx => {
        if (state.items[idx]) {
          state.items[idx][key] = value;
          const reviewed = Array.isArray(state.items[idx].reviewedFields) ? state.items[idx].reviewedFields : [];
          const changed = key === 'qty' ? ['qty', 'qtyRange', 'qtySource', 'countConfidence'] : [key];
          state.items[idx].reviewedFields = [...new Set([...reviewed, ...changed])];
          if (key === 'qty') { delete state.items[idx].qtyRange; state.items[idx].qtySource = 'stated'; state.items[idx].countConfidence = 'high'; }
        }
      });
      state.optimiseDirty = true; render(); scheduleRefresh();
    }
    function fz(labelText, value, onChange){
      return el('label',{class:'ai-fz'},[
        el('span',{class:'ai-fz__label'},[labelText]),
        el('input',{type:'number',inputmode:'numeric',value:value,onchange:onChange})
      ]);
    }

    // Module names are product titles, already cased in Shopify: title-casing them read "118 Mm".
    const trayLabel = (t) => t ? (t.name ? String(t.name).trim() : `${t.baseH}×${t.baseW}`) : '';

    const rows = groups.map((g, i) => {
      const members = Array.isArray(g.members) ? g.members : [];
      const choice = itemTray[members[0]] || null;
      const open = state.openRow === i;

      const numBadge = el('span',{class:'ai-item__num'},[String(i+1)]);
      const nameText = members.length
        // A described item shows the customer's words (label); its name is what the engine reads.
        ? members.map(idx => formatDisplayName((baseItems[idx] || {}).label || (baseItems[idx] || {}).name || g.name)).join(' + ')
        : formatDisplayName(g.name);
      const nameEl = el('div',{class:'ai-item__name'},[
        el('span',{class:'ai-item__nametext'},[nameText])
      ]);
      // Lead with the module/tray (the result the user is confirming), not mm. In
      // the grouped list the group's header names the tray, so the row leaves the
      // room to the item's name.
      const modPill = null;

      // Compact summary line (≈one row tall). Click to expand this row only.
      const summary = el('button',{type:'button',class:'ai-item__summary','aria-expanded':String(open),
        id:'ai-item-summary-'+i, 'aria-controls':'ai-item-panel-'+i,
        onclick:()=>{ state.selectedItem = i; state.openRow = open ? null : i; render(); document.getElementById('ai-item-summary-' + i)?.focus({ preventScroll: true }); }},[
        numBadge,
        el('div',{class:'ai-item__sum'},[ nameEl, modPill ]),
        el('span',{class:'ai-item__chev','aria-hidden':'true'},[open ? '▾' : '▸'])
      ]);

      function adjustQuantity(delta) {
        state.selectedItem = i;
        commitField(g, 'qty', Math.max(1, (g.qty || 1) + delta));
        document.querySelector('[data-quantity-action="' + i + ':' + delta + '"]')?.focus({ preventScroll: true });
      }
      const quantity = el('div', { class: 'ai-qty-stepper' }, [
        el('button', { type: 'button', 'data-quantity-action': i + ':-1', 'aria-label': mdT('modu.builder_js.quantity_less', { item: nameText }), disabled: (g.qty || 1) <= 1, onclick: () => adjustQuantity(-1) }, ['−']),
        // "~" + amber = the count came from a stacked/hidden pile (vision
        // countConfidence:low) — worth a tap to check before trusting the layout.
        (baseItems[members[0]] || {}).countConfidence === 'low'
          ? el('span',{class:'ai-item__qty ai-item__qty--est', title:mdT('modu.builder_js.estimated_quantity')},[members.length === 1 && (baseItems[members[0]] || {}).qtyRange ? String(baseItems[members[0]].qtyRange.min) + '–' + String(baseItems[members[0]].qtyRange.max) : '~' + (g.qty || 1)])
          : el('span',{class:'ai-item__qty', title:mdT('modu.builder_js.quantity')},['×' + (g.qty || 1)]),
        el('button', { type: 'button', 'data-quantity-action': i + ':1', 'aria-label': mdT('modu.builder_js.quantity_more', { item: nameText }), onclick: () => adjustQuantity(1) }, ['+'])
      ]);

      let panel = null;
      if (open) {
        const removeBtn = el('button',{type:'button',class:'ai-item__remove','aria-label':mdT('modu.builder_js.remove_item'),
          onclick: async ()=>{
            const indices = [...members];
            indices.sort((a,b) => b-a).forEach(idx => { if (idx >= 0 && idx < baseItems.length) state.items.splice(idx,1); });
            state.openRow = null;
            if (!state.items.length) { ++optimiseSeq; state.optionSet = null; state.optimiser = null; state.optimiseDirty = false; saveStateToStorage(); render(); return; }
            state.optimiseDirty = true; await refreshLayout();
          }},[mdT('modu.builder_js.remove')]);

        // The engine chooses the trays for the whole drawer: show where this item
        // landed (and the product); editing size or quantity lays it out again.
        const productLink = (choice && choice.handle)
          ? el('a',{class:'ai-item__viewlink',href:mdRoot(`products/${choice.handle}`) + (choice.variantId ? `?variant=${choice.variantId}` : ''),target:'_blank',rel:'noopener'},[mdT('modu.builder_js.view_product')])
          : null;
        const topRow = el('div',{class:'ai-item__panelrow'},[
          el('span',{class:'ai-fz__label'},[mdT('modu.builder_js.placed_in')]),
          choice
            ? el('span',{class:'ai-item__pill'},[trayLabel(choice)])
            : el('span',{class:'ai-item__module--empty'},[hasLayout ? mdT('modu.builder_js.item_didnt_fit') : mdT('modu.builder_js.no_module_yet')]),
          productLink
        ]);

        panel = el('div',{class:'ai-item__panel',id:'ai-item-panel-'+i,role:'region','aria-labelledby':'ai-item-summary-'+i},[
          el('div',{class:'ai-item__fields'},[
            fz(mdT('modu.builder_js.length_mm'), g.approxLenMM, e=>{ commitField(g,'approxLenMM', Math.max(0, +e.target.value||0)); }),
            fz(mdT('modu.builder_js.width_mm'), g.approxWidMM||0, e=>{ commitField(g,'approxWidMM', Math.max(0, +e.target.value||0)); }),
            fz(mdT('modu.builder_js.qty_label'), g.qty||1, e=>{ commitField(g,'qty', Math.max(1, Math.round(+e.target.value||1))); }),
            removeBtn
          ])
        ]);
      }

      return { node: el('div',{class:'ai-item' + (open ? ' is-open' : '') + (i === state.selectedItem ? ' is-active' : '') + (choice ? '' : ' ai-item--nomatch'), 'data-inventory-item': i},[ el('div', { class: 'ai-item__line' }, [summary, quantity]), panel ]), fitted: !!choice, tray: choice };
    });

    // Summary header (trays / coverage / drawer used + an "optimising" state).
    let header = null;
    if (hasLayout) {
      const s = state.optimiser.stats || {};
      const trays = (s.moduleCount != null) ? s.moduleCount : (state.optimiser.selection.modules || []).length;
      const covered = (s.itemsCoveredPct != null) ? Math.round(s.itemsCoveredPct) : null;
      const used = (s.wastePct != null) ? Math.max(0, 100 - Math.round(s.wastePct)) : null;
      const bits = [mdT('modu.builder_js.tray_count', { count: trays })];
      if (covered != null) bits.push(mdT('modu.builder_js.items_fit_percent', { percent: covered }));
      if (used != null) bits.push(mdT('modu.builder_js.drawer_used_percent', { percent: used }));
      header = el('div',{class:'ai-optsum' + (busy ? ' is-busy' : '')},[
        el('span',{class:'ai-optsum__label'},[ busy ? mdT('modu.builder_js.optimising') : mdT('modu.builder_js.optimised_layout') ]),
        busy ? null : el('span',{class:'ai-optsum__stats'},[ bits.join(' · ') ])
      ]);
    } else if (busy) {
      header = el('div',{class:'ai-optsum is-busy'},[ el('span',{class:'ai-optsum__label'},[ mdT('modu.builder_js.optimising') ]) ]);
    }

    let warn = null;
    if (!hasLayout && !busy && baseItems.length) {
      // No layout came back (the engine could not design the drawer, or the
      // Worker did not answer): say so; Generate tries again.
      warn = el('div',{class:'ai-optwarn', role:'status'},[
        el('span',{class:'ai-optwarn__icon','aria-hidden':'true'},['⚠']),
        el('span',{class:'ai-optwarn__text'},[ mdT('modu.builder_js.generate_failed') ])
      ]);
    } else if (hasLayout && !busy) {
      // Partial-fill warning: an aggregate, touch-visible signal that some items
      // were dropped (the per-row "Didn't fit" pill alone is easy to miss on mobile).
      const uncoveredCount = baseItems.length - Object.keys(itemTray).length;
      if (uncoveredCount > 0) {
        // Don't reflexively blame the drawer: piled-cutlery quantities are often
        // over-estimates (marked "~"), and correcting one count usually makes
        // everything fit. Suggest a larger drawer only as the last option.
        const lowConf = baseItems.some(it => it && it.countConfidence === 'low');
        const msg = lowConf
          ? mdT('modu.builder_js.items_not_fit_estimated', { count: uncoveredCount })
          : mdT('modu.builder_js.items_not_fit', { count: uncoveredCount });
        warn = el('div',{class:'ai-optwarn', role:'status'},[
          el('span',{class:'ai-optwarn__icon','aria-hidden':'true'},['⚠']),
          el('span',{class:'ai-optwarn__text'},[ msg ])
        ]);
      }
    }

    // Segment the list when some items didn't fit: the misses surface directly
    // under the warning banner (the thing to act on), the placed items sit below,
    // one group per tray: the tray's name and product link, then the items that
    // share it (numbered as on the photo).
    let body;
    const misses = rows.filter(r => !r.fitted);
    const fits = rows.filter(r => r.fitted);
    if (grouped) {
      const byTray = new Map(trayOrder.map(key => [key, []]));
      fits.forEach(r => { const list = byTray.get(r.tray.key); if (list) list.push(r); });
      const trayGroups = trayOrder.filter(key => byTray.get(key).length).map(key => {
        const list = byTray.get(key);
        const tray = list[0].tray;
        const productLink = tray.handle
          ? el('a',{class:'ai-modgroup__link',href:mdRoot(`products/${tray.handle}`) + (tray.variantId ? `?variant=${tray.variantId}` : ''),target:'_blank',rel:'noopener'},[mdT('modu.builder_js.view_product')])
          : null;
        return el('div',{class:'ai-modgroup'},[
          el('div',{class:'ai-modgroup__hdr'},[ el('span',{class:'ai-modgroup__name'},[ trayLabel(tray) ]), productLink ]),
          ...list.map(r => r.node)
        ]);
      });
      body = misses.length && fits.length
        ? [
            el('div',{class:'ai-group-hdr ai-group-hdr--warn'},[mdT('modu.builder_js.didnt_fit_group', { count: misses.length })]),
            ...misses.map(r => r.node),
            el('div',{class:'ai-group-hdr'},[mdT('modu.builder_js.in_layout_group', { count: fits.length })]),
            ...trayGroups
          ]
        : [...misses.map(r => r.node), ...trayGroups];
    } else {
      body = rows.map(r => r.node);
    }
    return el('div',{class:'ai-items'}, [ busy ? header : null, warn, ...body ]);
  }

  async function render() {
    const root = document.getElementById(MOUNT_ID);
    if (!root) return;
    if (state.optionSet && state.optionInputs !== optionInputKey()) {
      state.optionSet = null; state.accountSave = '';
      if (state.step === 'options') state.step = 'items';
    }
    const scrollY = window.scrollY;
    root.innerHTML = '';

    // Clean up any stale full-screen overlay from older builds
    // Remove stale overlays if their state has changed
    const staleAnalyzing = document.getElementById('ai-analyzing-overlay');
    if (staleAnalyzing && !state.analyzing) try { staleAnalyzing.remove(); } catch(_) {}
    const staleGenerating = document.getElementById('ai-generating-overlay');
    if (staleGenerating && !state.generatingUrl) try { staleGenerating.remove(); } catch(_) {}

    const page = el('div',{class:'ai-shell',style:{position:'relative'}},[]);

    // ----- Step 1: drawer size -----
    let analyzeBtnEl = null; // assigned below
    function canAnalyzeNow(){
      // Text mode: a description is enough; the size can come from it (designFromDescription).
      if (TEXT_MODE) return String(state.description || '').trim().length >= DESCRIBE_MIN_CHARS && (textHeightBlank() || normaliseDrawerInteriorHeightMm(state.H) != null);
      return !!((state.imageFile || state.photoRead) && state.L>0 && state.W>0 && normaliseDrawerInteriorHeightMm(state.H) != null);
    }
    function updateAnalyzeButton(){ if (analyzeBtnEl){ analyzeBtnEl.disabled = !canAnalyzeNow() || (TEXT_MODE && state.analyzing); } }

    // The fields keep their elements so a converted size can be written back in
    // place: a full render would drop the keyboard focus while typing.
    const dimInputs = {};
    function dimField(key, labelText, readInput, inputAttrs){
      // Text mode: an empty field reads empty, not 0 (it may be left for the description to fill).
      const shown = !(+state[key] > 0) ? '' : state[key];
      const input = el('input',Object.assign({type:'number',inputmode:'decimal',min:1,step:'any',value:shown,
        oninput:e=>{ if (state[key] !== readInput(e.target.value)) { if (!TEXT_MODE) invalidatePhoto(); else { state.optionSet = null; state.optimiseDirty = true; } } state[key] = readInput(e.target.value); saveStateToStorage(); updateAnalyzeButton(); updateSizeHint(); }}, inputAttrs || {}));
      dimInputs[key] = input;
      return el('label',{class:'ai-field'},[
        el('span',{class:'ai-field__label'},[labelText]),
        input
      ]);
    }
    // A size typed in centimetres makes a drawer smaller than a module and an
    // empty layout. Say so under the fields and offer the millimetre value; the
    // visitor can keep what they typed (assets/md-size-units.js has the rule).
    const sizeHint = el('div',{class:'ai-sizehint',role:'status','aria-live':'polite',hidden:true},[]);
    // Text mode: "Design my layout" found no size; the note goes once both sides are typed.
    const sizeNeededEl = (TEXT_MODE && state.sizeNeeded)
      ? el('p',{class:'ai-note ai-reading__note--warn ai-sizeneeded',role:'alert'},[mdT('modu.builder_js.describe_size_needed')])
      : null;
    function updateSizeHint(){
      const summary = document.querySelector('[data-drawer-size]');
      if (summary) { summary.hidden = !(state.W > 0 && state.L > 0); summary.textContent = [state.W,state.L,state.H].filter(v => +v > 0).join(' × ') + ' mm'; }
      const mdSizeUnits = sizeUnits();
      const found = mdSizeUnits ? mdSizeUnits.check({ L: state.L, W: state.W, H: state.H }) : null;
      if (sizeNeededEl) sizeNeededEl.hidden = state.L > 0 && state.W > 0;
      while (sizeHint.firstChild) sizeHint.removeChild(sizeHint.firstChild);
      sizeHint.hidden = !found;
      if (!found) return;
      if (found.kind === 'cm') {
        const size = mdSizeUnits.label(found.size);
        sizeHint.appendChild(el('span',{class:'ai-sizehint__text'},[mdSizeUnits.noBreak(mdT('modu.builder_js.size_hint_cm', { size }))]));
        sizeHint.appendChild(el('button',{type:'button',class:'ai-sizehint__btn',onclick:()=>{
          if (!TEXT_MODE) invalidatePhoto();
          ['L','W','H'].forEach(key => {
            state[key] = found.size[key];
            if (dimInputs[key]) dimInputs[key].value = found.size[key] || '';
          });
          saveStateToStorage();
          updateAnalyzeButton();
          updateSizeHint();
          // The button is gone with the hint: hand the focus to the next step.
          const next = (analyzeBtnEl && !analyzeBtnEl.disabled) ? analyzeBtnEl : dimInputs.L;
          if (next && typeof next.focus === 'function') next.focus();
        }},[mdSizeUnits.noBreak(mdT('modu.builder_js.size_hint_cm_apply', { size }))]));
        return;
      }
      const text = found.kind === 'height' ? mdT('modu.builder_js.size_hint_height') : mdT('modu.builder_js.size_hint_small');
      sizeHint.appendChild(el('span',{class:'ai-sizehint__text'},[text]));
    }
    const dims = el('div',{class:'ai-dims'},[
      dimField('L', mdT('modu.builder_js.length_mm'), value => +value||0),
      dimField('W', mdT('modu.builder_js.width_mm'), value => +value||0),
      // The height stays as typed: normaliseDrawerInteriorHeightMm reads the text.
      dimField(
        'H',
        TEXT_MODE ? mdT('modu.builder_js.describe_height_optional') : mdT('modu.builder_js.height_mm'),
        value => value,
        { min: MIN_DRAWER_HEIGHT_MM, max: MAX_DRAWER_INTERIOR_HEIGHT_MM, step: 0.1, inputmode: 'decimal', 'aria-describedby': 'ai-height-note' }
      ),
      el('p',{class:'ai-height-note',id:'ai-height-note'},[TEXT_MODE ? mdT('modu.builder_js.describe_height_note') : mdT('modu.builder_js.height_limit_note')])
    ]);
    updateSizeHint();

    // ----- Step: photo (photo-first flow) -----
    const uploadBox = el('div',{class:'ai-dropzone',style:{display: state.imageFile ? 'none' : 'flex'},
      onclick:()=>{ const inp=document.getElementById('ai-photo-input'); if(inp) inp.click(); }},[
      el('div',{class:'ai-dropzone__icon','aria-hidden':'true'},['+']),
      el('div',{class:'ai-dropzone__title'},[mdT('modu.builder_js.add_photo_title')]),
      el('div',{class:'ai-dropzone__hint'},[mdT('modu.builder_js.add_photo_hint')]),
      el('input',{type:'file',accept:'image/*',style:{display:'none'},id:'ai-photo-input',onchange:e=>{ invalidatePhoto(); const f = e.target.files && e.target.files[0]; if (state.imageUrl) { try { URL.revokeObjectURL(state.imageUrl); } catch(_){} } state.imageFile = f || null; state.imageUrl = f ? URL.createObjectURL(f) : ''; state.step = 'photo'; saveStateToStorage(); void keepPhoto(state.imageFile); render(); }}),
      el('button',{type:'button',class:'ai-btn ai-btn--primary',onclick:e=>{ e.stopPropagation(); document.getElementById('ai-photo-input').click(); }},[mdT('modu.builder_js.choose_photo')])
    ]);

    const previewImg = el('img',{class:'ai-preview__img',src: state.imageUrl || '', alt:mdT('modu.builder_js.photo_preview_alt')});
    const removeBtn = el('button',{type:'button',class:'ai-btn ai-btn--secondary',onclick:()=>{ invalidatePhoto(); if (state.imageUrl) { try { URL.revokeObjectURL(state.imageUrl); } catch(_){} } state.imageFile=null; state.imageUrl=''; const inp=document.getElementById('ai-photo-input'); if(inp) inp.value=''; state.step='photo'; saveStateToStorage(); void keepPhoto(null); render(); }},[mdT('modu.builder_js.retake_remove')]);
    const previewWrap = el('div',{class:'ai-preview',style:{display: state.imageFile ? 'block' : 'none'}},[
      state.imageUrl ? renderDrawerSummary() : previewImg,
      el('div',{class:'ai-action-row'},[removeBtn])
    ]);
    const cardPhoto = el('section',{class:'ai-card ai-photo-start'},[
      el('div',{class:'ai-step'},[mdT('modu.builder_js.workspace_photo_title')]),
      el('div',{class:'ai-photo-start__image'},[uploadBox, previewWrap]),
      ...(!TEXT_MODE ? [el('div',{class:'ai-photo-start__measure'},[el('h2',{},[mdT('modu.builder_js.workspace_size')]),el('p',{class:'ai-note'},[mdT('modu.builder_js.measure_inside')]), dims, sizeHint])] : [])
    ]);

    // ----- Step: dimensions -----
    const canAnalyze = canAnalyzeNow();
    analyzeBtnEl = TEXT_MODE
      ? el('button',{type:'button',class:'ai-btn ai-btn--primary',onclick:designFromDescription,disabled:!canAnalyze || state.analyzing},[ state.analyzing ? mdT('modu.builder_js.describe_designing') : mdT('modu.builder_js.describe_design') ])
      : el('button',{type:'button',class:'ai-btn ai-btn--primary',onclick:analyze,disabled:!canAnalyze},[mdT('modu.builder_js.photo_continue')]);
    // Text mode: the size fields sit on the describe card instead.
    const briefArea = el('textarea', { id:'ai-photo-description', class:'ai-describe__input', rows:'5', maxlength:String(DESCRIBE_MAX_CHARS),
      disabled: !!state.briefTask, oninput:e=>{ updateDescription(e.target.value); updatePhotoActivity(); } });
    briefArea.value = state.description;
    const cardBrief = el('section',{class:'ai-card ai-photo-brief'},[
      el('label',{class:'ai-photo-brief__label',for:'ai-photo-description'},[mdT('modu.builder_js.describe_label')]),
      el('span',{class:'ai-note'},[mdT('modu.builder_js.photo_optional')]),
      briefArea,
      el('div',{class:'ai-photo-activity',id:'ai-photo-activity',role:'status','aria-live':'polite'},[
        el('span',{class:'ai-photo-dots','aria-hidden':'true'},[el('i'),el('i'),el('i')]),
        el('span',{'data-photo-status':''},[])
      ])
    ]);

    // ----- Step: describe (text mode) -----
    let cardDescribe = null;
    if (TEXT_MODE) {
      const counter = el('span',{class:'ai-describe__count','aria-live':'polite'},[
        mdT('modu.builder_js.describe_count', { count: String(state.description.length), max: String(DESCRIBE_MAX_CHARS) })
      ]);
      const area = el('textarea',{
        id:'ai-describe-input', class:'ai-describe__input', rows:'6', maxlength:String(DESCRIBE_MAX_CHARS),
        placeholder: mdT('modu.builder_js.describe_placeholder'),
        'aria-describedby':'ai-describe-hint ai-describe-privacy',
        oninput: e => {
          updateDescription(e.target.value);
          counter.textContent = mdT('modu.builder_js.describe_count', { count: String(state.description.length), max: String(DESCRIBE_MAX_CHARS) });
          updateAnalyzeButton();
        }
      });
      area.value = state.description;
      const EXAMPLES = [
        [mdT('modu.builder_js.describe_example_1_label'), mdT('modu.builder_js.describe_example_1_text')],
        [mdT('modu.builder_js.describe_example_2_label'), mdT('modu.builder_js.describe_example_2_text')],
        [mdT('modu.builder_js.describe_example_3_label'), mdT('modu.builder_js.describe_example_3_text')]
      ];
      const examples = EXAMPLES.map(([label, text]) => el('button',{type:'button',class:'ai-alt',
        onclick:()=>{
          updateDescription(text);
          render();
          const input = document.getElementById('ai-describe-input');
          if (input) input.focus();
        }},[label]));
      cardDescribe = el('section',{class:'ai-card ai-describe'},[
        el('div',{class:'ai-step'},[mdT('modu.builder_js.describe_step')]),
        el('label',{class:'ai-field__label',for:'ai-describe-input'},[mdT('modu.builder_js.describe_label')]),
        el('p',{class:'ai-note',id:'ai-describe-hint'},[mdT('modu.builder_js.describe_hint')]),
        area,
        state.reading?.status === 'failed' ? el('p',{class:'ai-note ai-reading__note--warn',role:'alert'},[mdT('modu.builder_js.describe_failed_note')]) : null,
        el('div',{class:'ai-describe__meta'},[counter]),
        el('div',{class:'ai-describe__examples'},[
          el('span',{class:'ai-companions__label'},[mdT('modu.builder_js.describe_examples_label')]),
          el('div',{class:'ai-alts'}, examples)
        ]),
        // The drawer's size, on the same card: typed here, or read from the text when left empty.
        el('div',{class:'ai-describe__size'},[
          el('div',{class:'ai-describe__size-title'},[mdT('modu.builder_js.describe_size_heading')]),
          el('p',{class:'ai-note ai-describe__size-note'},[mdT('modu.builder_js.describe_size_note')]),
          dims,
          sizeHint,
          sizeNeededEl
        ]),
        el('p',{class:'ai-note ai-describe__privacy',id:'ai-describe-privacy'},[mdT('modu.builder_js.describe_privacy')])
      ]);
    }
    updateAnalyzeButton();

    window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'render:canPlan', {
      step: state.step,
      items: state.items.length,
      L: state.L,
      W: state.W,
      analyzing: state.analyzing
    });

    // ----- Step: detected items -----
    const addSelect = (() => {
      const sel = el('select',{id:'ai-add-item-select',class:'ai-select'},[]);
      sel.appendChild(el('option',{value:''},[mdT('modu.builder_js.add_utensil_option')]));
      (state.taxonomy || []).forEach(t => {
        const label = formatDisplayName(t.objectName || t.handle || '');
        sel.appendChild(el('option',{value:String(t.id)},[label]));
      });
      return sel;
    })();
    const addRow = el('div',{class:'ai-add-row'},[
      addSelect,
      el('button',{
        type:'button',
        class:'ai-btn ai-btn--secondary',
        onclick: async ()=>{
          const sel = document.getElementById('ai-add-item-select');
          if (!sel || !sel.value) return;
          const taxId = sel.value;
          const t = (state.taxonomy || []).find(x => String(x.id) === String(taxId));
          const name = t ? (t.objectName || t.handle || mdT('modu.builder_js.custom_utensil')) : mdT('modu.builder_js.custom_utensil');
          // Its taxonomy id travels; a curated typical size fills the length (0 lets the engine size it).
          state.items.push({ name, taxonomyId: t ? String(t.id) : null, approxLenMM: (t && +t.typicalLengthMM) || 0, approxWidMM: (t && +t.typicalWidthMM) || 0, qty: 1 });
          // Lay the drawer out again so the new item gets a module.
          state.step = 'items';
          sel.value = '';
          state.optimiseDirty = true; await refreshLayout();
        }
      },[mdT('modu.builder_js.add')])
    ]);
    // Companion chips: cutlery comes in sets, and the set-mate is the #1 vision miss
    // (dessert forks stacked under dinner forks, etc.). When a "when" item is present
    // and its companion is absent, offer a one-tap add prefilled with typical dims and
    // a qty mirroring the source item — no 0×0 degraded state, no typing.
    const COMPANIONS = [
      { when: 'teaspoons',     suggest: 'dessert-forks', len: 145, wid: 25 },
      { when: 'dinner-knives', suggest: 'dinner-forks',  len: 190, wid: 25 },
      { when: 'dinner-forks',  suggest: 'dinner-knives', len: 225, wid: 20 },
      { when: 'tablespoons',   suggest: 'teaspoons',     len: 145, wid: 30 },
      { when: 'corkscrew',     suggest: 'bottle-opener', len: 120, wid: 40, qty: 1 }
    ];
    function companionRow(){
      if (!state.items || !state.items.length || !state.taxonomy || !state.taxonomy.length) return null;
      const present = new Set(state.items.map(it => taxonomyKeyForItem(it)).filter(Boolean));
      const byName = n => (state.taxonomy || []).find(t => String(t.objectName || '').toLowerCase() === n);
      const chips = [];
      const offered = new Set();
      for (const c of COMPANIONS) {
        if (chips.length >= 3) break;
        const w = byName(c.when), s = byName(c.suggest);
        if (!w || !s || offered.has(s.id)) continue;
        if (!present.has(w.id) || present.has(s.id)) continue;
        offered.add(s.id);
        const src = state.items.find(it => taxonomyKeyForItem(it) === w.id);
        const qty = c.qty || Math.max(1, Math.round((src && src.qty) || 1));
        chips.push(el('button',{type:'button',class:'ai-alt',
          onclick: async ()=>{
            // countConfidence 'low' -> the "~" badge invites a count check.
            state.items.push({ name: s.objectName || c.suggest, approxLenMM: c.len, approxWidMM: c.wid, qty, countConfidence: 'low' });
            state.optimiseDirty = true; await refreshLayout();
          }},
          ['+ ' + formatDisplayName(s.objectName || c.suggest) + ' ×' + qty]));
      }
      if (!chips.length) return null;
      return el('div',{class:'ai-companions'},[
        el('span',{class:'ai-companions__label'},[mdT('modu.builder_js.often_found_together')]),
        el('div',{class:'ai-alts'}, chips)
      ]);
    }

    const hasItems = !!(state.items && state.items.length);
    // Text mode: what the reading said, above the items (a failed reading, words
    // it could not place, assumed counts and typical sizes).
    const readingNoteEls = [];
    if (TEXT_MODE && state.reading && state.reading.status !== 'reading') {
      if (state.reading.status === 'failed') {
        readingNoteEls.push(el('p',{class:'ai-note ai-reading__note ai-reading__note--warn',role:'status'},[mdT('modu.builder_js.describe_failed_note')]));
      } else if (!hasItems) {
        readingNoteEls.push(el('p',{class:'ai-note ai-reading__note ai-reading__note--warn',role:'status'},[mdT('modu.builder_js.describe_empty_note')]));
      }
      if (state.reading.status === 'ok') {
        readingNotes(state.reading.result, state.items).forEach(note => {
          const NOTE_TEXT = {
            unrecognised: () => mdT('modu.builder_js.describe_unrecognised', { items: note.items }),
            assumed_counts: () => mdT('modu.builder_js.describe_assumed_counts'),
            typical_sizes: () => mdT('modu.builder_js.describe_typical_sizes')
          };
          if (NOTE_TEXT[note.key]) readingNoteEls.push(el('p',{class:'ai-note ai-reading__note'},[NOTE_TEXT[note.key]()]));
        });
      }
    }
    const cardItems = el('section',{class:'ai-card ai-review-card'},[
      el('div',{class:'ai-step'},[mdT('modu.builder_js.workspace_items')]),
      el('p',{class:'ai-note'},[mdT('modu.builder_js.workspace_review_note')]),
      readingNoteEls.length ? el('div',{class:'ai-reading'}, readingNoteEls) : null,
      hasItems
        ? renderItemsTable()
        : (TEXT_MODE ? null : el('p',{class:'ai-empty'},[mdT('modu.builder_js.no_items_review')])),
      hasItems ? companionRow() : null,
      addRow
    ]);

    const layoutCard = state.optionSet ? renderOptions() : null;

    // ----- CTA bar -----
    const actions = el('div', { class:'ai-cta-bar' }, [
      el('span',{class:'ai-coverage'},[mdT('modu.builder_js.options_before')]),
      state.layoutError ? el('p',{id:'ai-layout-error',class:'ai-layout-error',role:'alert'},[mdT('modu.builder_js.url_failed')]) : null,
      el('div',{class:'ai-cta-bar__btns'},[
        el('button',{
          id:'ai-generate-open',
          class:'ai-btn ai-btn--primary',
          type:'button',
          // Gate on an in-flight/pending optimise so Generate can never redirect to a
          // stale pre-edit layout (the hash is only fresh once the optimiser settles).
          disabled: !hasItems || state.descriptionDirty || state.generatingUrl || state.optimising || state.optimiseDirty
        },[ state.generatingUrl ? mdT('modu.builder_js.generating') : ((state.optimising || state.optimiseDirty) ? mdT('modu.builder_js.optimising') : mdT('modu.builder_js.options_generate')) ])
      ])
    ]);

    // ----- Step machine (photo + size → description → items; text mode: description + size → items) -----
    const STEPS = TEXT_MODE ? ['describe', 'items', 'options'] : ['photo', 'brief', 'items', 'options'];
    if (STEPS.indexOf(state.step) < 0) state.step = STEPS[0];
    const stepIdx = STEPS.indexOf(state.step);
    const canGo = navigationAvailability();
    function goStep(s){ if (['items','options'].includes(s) && state.descriptionDirty) return; if (s === 'options' && !state.optionSet) return; if (navigationAvailability()[s]) { if (!TEXT_MODE && state.briefTask) { state.briefTask = null; ++optimiseSeq; } state.step = s; saveStateToStorage(); render(); if (s === 'items' && state.optimiseDirty && !state.optimising) refreshLayout(); } }

    const STEP_LABELS = TEXT_MODE
      ? [['describe',mdT('modu.builder_js.describe_step_short')], ['items',mdT('modu.builder_js.workspace_items')], ['options',mdT('modu.builder_js.workspace_layouts')]]
      : [['photo',mdT('modu.builder_js.step_photo_short')], ['brief',mdT('modu.builder_js.describe_step_short')], ['items',mdT('modu.builder_js.workspace_items')], ['options',mdT('modu.builder_js.workspace_layouts')]];
    const stepper = el('nav', { class:'ai-progress', 'aria-label':mdT('modu.builder_js.progress') },
      STEP_LABELS.map(([key, label], i) => el('button', { type:'button', class:'ai-progress__step' + (i === stepIdx ? ' is-current' : '') + (i < stepIdx ? ' is-done' : ''),
        disabled: !canGo[key], 'aria-current': i === stepIdx ? 'step' : 'false', 'aria-label': (i + 1) + ' / ' + STEPS.length + ': ' + label, onclick:() => goStep(key)
        , ...(key === 'items' || key === 'options' ? {'data-ai-forward':key} : {})
      }, [el('span',{class:'ai-progress__bar','aria-hidden':'true'}), el('span',{class:'ai-progress__label','aria-hidden':'true'},[(i + 1) + ' · ' + label])]))
    );

    // Contextual nav (Back + the step's primary action).
    const navKids = [];
    if (stepIdx > 0 && state.step !== 'options') {
      navKids.push(el('button',{type:'button',class:'ai-btn ai-btn--secondary',
        onclick:()=>goStep(STEPS[Math.max(0, stepIdx-1)])},[mdT('modu.builder_js.back')]));
    }
    if (state.step === 'describe') {
      navKids.push(analyzeBtnEl);
      if (state.reading?.status === 'failed') navKids.push(el('button',{type:'button',class:'ai-btn ai-btn--secondary',onclick:reviewPhotoManually},[mdT('modu.builder_js.photo_manual')]));
      if (hasItems && !state.descriptionDirty) {
        navKids.push(el('button',{type:'button',class:'ai-btn ai-btn--secondary','data-ai-forward':'items',
          onclick:()=>goStep('items')},[mdT('modu.builder_js.skip_items')]));
      }
    } else if (state.step === 'photo') {
      navKids.push(analyzeBtnEl);
    } else if (state.step === 'brief') {
      navKids.push(el('button',{type:'button',id:'ai-photo-continue',class:'ai-btn ai-btn--primary',disabled:!!state.briefTask,onclick:continuePhoto},[mdT('modu.builder_js.photo_continue')]));
      navKids.push(el('button',{type:'button',id:'ai-photo-manual',class:'ai-btn ai-btn--secondary',hidden:true,onclick:reviewPhotoManually},[mdT('modu.builder_js.photo_manual')]));
    }
    const navBar = navKids.length
      ? el('div',{class:'ai-nav'}, navKids)
      : null;

    const activeCard = state.step === 'photo' ? cardPhoto
      : state.step === 'describe' ? cardDescribe
      : state.step === 'brief' ? cardBrief
      : state.step === 'options' ? layoutCard
      : cardItems;

    // Slide the new stage in (mirrors the swipe-sheet track motion) — only when the
    // STEP actually changed, not on in-step re-renders (field edits, accordion, etc.).
    const prevIdx = (typeof state.lastStepIdx === 'number') ? state.lastStepIdx : stepIdx;
    if (stepIdx !== prevIdx) activeCard.className += (stepIdx > prevIdx ? ' ai-card--in-right' : ' ai-card--in-left');
    state.lastStepIdx = stepIdx;

    page.appendChild(stepper);
    page.appendChild(el('p',{id:'ai-storage-warning',class:'ai-save-warning',role:'status',hidden:!state.storageFailed},[mdT('modu.builder_js.workspace_storage_failed')]));
    if (state.step === 'items' || state.step === 'brief') {
      const workspace = el('div', {class:'ai-workspace' + (TEXT_MODE ? ' is-text' : state.photoCollapsed ? ' is-photo-hidden' : '')}, [renderDrawerSummary(), el('div',{class:'ai-workspace__main'},[activeCard, state.step === 'items' ? actions : null, navBar])]);
      page.appendChild(workspace);
    } else page.appendChild(activeCard);
    // Text mode: keep the layout (email or link), and say so after a newsletter sign-up.
    if (TEXT_MODE && state.step === 'options' && state.optimiser && state.optimiser.hash && !state.optimising && !state.optimiseDirty) {
      const lead = state.lead;
      const kids = [el('div',{class:'ai-step'},[mdT('modu.builder_js.describe_lead_heading')])];
      if (subscribedOnLoad) kids.push(el('p',{class:'ai-note ai-lead__done',role:'status'},[mdT('modu.builder_js.describe_subscribed')]));
      if (EMAIL_LAYOUT && lead.status !== 'sent') {
        const input = el('input',{type:'email',id:'ai-lead-email',class:'ai-lead__input',autocomplete:'email',inputmode:'email',
          placeholder: mdT('modu.builder_js.describe_lead_placeholder'),
          'aria-invalid': lead.status === 'invalid' ? 'true' : 'false', 'aria-describedby':'ai-lead-note',
          oninput: e => {
            lead.email = String(e.target.value || '').slice(0, 254);
            // A correction clears the error in place (a full render would drop the focus).
            if (lead.status === 'invalid') {
              lead.status = 'idle';
              e.target.setAttribute('aria-invalid', 'false');
              const error = e.target.closest('.ai-lead') && e.target.closest('.ai-lead').querySelector('.ai-lead__error');
              if (error) error.remove();
            }
          }});
        input.value = lead.email;
        const box = el('input',{type:'checkbox',id:'ai-lead-marketing',onchange: e => { lead.marketing = !!e.target.checked; }});
        box.checked = !!lead.marketing;
        kids.push(el('label',{class:'ai-field__label',for:'ai-lead-email'},[mdT('modu.builder_js.describe_lead_label')]));
        kids.push(el('div',{class:'ai-lead__row'},[
          input,
          el('button',{type:'button',class:'ai-btn ai-btn--primary ai-lead__send',disabled: lead.status === 'sending',onclick: sendLayoutEmail},[
            lead.status === 'sending' ? mdT('modu.builder_js.describe_lead_sending') : mdT('modu.builder_js.describe_lead_send')
          ])
        ]));
        if (lead.status === 'invalid') kids.push(el('p',{class:'ai-note ai-lead__error',role:'alert'},[mdT('modu.builder_js.describe_lead_invalid')]));
        if (lead.status === 'failed') kids.push(el('p',{class:'ai-note ai-lead__error',role:'alert'},[mdT('modu.builder_js.describe_lead_failed')]));
        kids.push(el('p',{class:'ai-note',id:'ai-lead-note'},[mdT('modu.builder_js.describe_lead_note')]));
        kids.push(el('label',{class:'ai-lead__consent',for:'ai-lead-marketing'},[box, el('span',{},[mdT('modu.builder_js.describe_lead_marketing')])]));
      } else if (EMAIL_LAYOUT && lead.status === 'sent') {
        kids.push(el('p',{class:'ai-note ai-lead__done',role:'status'},[mdT('modu.builder_js.describe_lead_sent')]));
      }
      // The link always works, and is the fallback when the email could not go out.
      kids.push(el('button',{type:'button',class:'ai-btn ai-btn--secondary ai-lead__copy',onclick: copyLayoutLink},[
        lead.copied ? mdT('modu.builder_js.describe_link_copied') : mdT('modu.builder_js.describe_copy_link')
      ]));
      page.appendChild(el('section',{class:'ai-card ai-lead'}, kids));
    }
    if (navBar && !['brief','items'].includes(state.step)) page.appendChild(navBar);
    root.appendChild(page);
    syncCustomDesignForm();
    const title = document.querySelector('#ai-layout-root .ai-title');
    const subtitle = document.querySelector('#ai-layout-root .ai-sub');
    const headingKeys = { photo: 'workspace_title', brief: 'book_brief_heading', items: 'workspace_review_title', options: 'options_title', describe: 'describe_step' };
    const subtitleKeys = { photo: 'workspace_subtitle', brief: 'book_brief_note', items: 'workspace_review_note', options: 'options_intro', describe: 'describe_hint' };
    if (title) title.textContent = workspaceT(headingKeys[state.step]);
    if (subtitle) subtitle.textContent = workspaceT(state.step === 'options' && state.optionSet?.options.length < 3 ? 'options_fewer' : subtitleKeys[state.step]);
    if (!TEXT_MODE) updatePhotoActivity();
    // A new stage starts at the tool; in-stage edits keep their scroll position.
    if (stepIdx !== prevIdx) root.scrollIntoView({block:'start'});
    else window.scrollTo(0, scrollY);

    // Swipe-to-advance between stages (mirrors the drawer-builder swipe tokens):
    // a horizontal drag past ~25% advances / goes back; vertical scroll is preserved
    // and interactive controls are left alone.
    setupStageSwipe(activeCard, stepIdx, STEPS, navigationAvailability, goStep);

    // Analyzing overlay (blocking)
    const analyzingOverlay = TEXT_MODE && state.analyzing
      ? el('div', { id:'ai-analyzing-overlay', class:'ai-think-overlay', role:'status', 'aria-live':'polite' }, [
          el('div', { class:'ai-think' }, [
            el('div', { class:'ai-think__orb ai-think__orb--analyze' }),
            el('div', { id:'ai-analyze-thinking', class:'ai-think__line' }, [
              (TEXT_MODE ? AI_DESCRIBE_MSGS : AI_ANALYZE_MSGS)[state.analyzingMsgIdx || 0]
            ]),
            el('div', { class:'ai-think__dots ai-think__dots--analyze' }, [
              el('span'), el('span'), el('span')
            ])
          ])
        ])
      : null;
    if (analyzingOverlay) page.appendChild(analyzingOverlay);

    // Generating URL overlay (blocking)
    const generatingOverlay = state.generatingUrl
      ? el('div', { id:'ai-generating-overlay', class:'ai-think-overlay', role:'status', 'aria-live':'polite' }, [
          el('div', { class:'ai-think ai-think--generate' }, [
            el('div', { class:'ai-think__orb ai-think__orb--generate' }),
            el('div', { id:'ai-generate-thinking', class:'ai-think__line' }, [
              AI_GENERATE_MSGS[state.generatingMsgIdx || 0]
            ]),
            el('div', { class:'ai-think__dots ai-think__dots--generate' }, [
              el('span'), el('span'), el('span')
            ])
          ])
        ])
      : null;
    if (generatingOverlay) page.appendChild(generatingOverlay);
    // The viewport status remains visible even when Generate was below a long list.
    // Keep keyboard users out of the temporarily blocked controls as well.
    const blockingOverlay = generatingOverlay || analyzingOverlay;
    if (blockingOverlay) [...page.children].forEach(node => { if (node !== blockingOverlay) node.inert = true; });
    root.setAttribute('aria-busy', String(!!blockingOverlay));


  }

  // Expose dev helpers for testing when debug mode is on
  if (DEBUG_LAYOUT && typeof window !== 'undefined') {
    // Expose state for debugging
    window.MD_getState = function() {
      return {
        L: state.L,
        W: state.W,
        H: state.H,
        items: state.items,
        optimiser: state.optimiser,
        taxonomy: state.taxonomy.length
      };
    };
  }

  // Delegate Generate Layout clicks so it always fires even if the button is re-rendered
  document.addEventListener('click', async (evt) => {
    const genOpen = evt.target.closest && evt.target.closest('#ai-generate-open');
    if (genOpen) {
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'generate+open:dispatch');
      // Generate compares the current inventory; opening happens only after a choice.
      await generateOptions();
      return;
    }
    // Back-compat: older split buttons still work if present in cached markup.
    const genBtn = evt.target.closest && evt.target.closest('#ai-generate-layout');
    if (genBtn) { refreshLayout(); return; }
    const openBtn = evt.target.closest && evt.target.closest('#ai-open-builder');
    if (openBtn) { openInBuilder(); }
  });

  document.addEventListener('DOMContentLoaded', async () => {
    window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', VERSION, 'DOMContentLoaded');
    
    // Initialize session ID from URL (or create new one)
    currentSessionId = getOrCreateSessionId();
    try {
      aiLayoutAnalytics = createAiLayoutAnalytics(
        window,
        window.sessionStorage,
        getStorageKey() + '-analytics-v1',
        generateUUID,
        TEXT_MODE ? 'describe_drawer' : 'ai_layout_creator'
      );
    } catch (_) {
      // Storage/privacy APIs are optional dependencies. A failure must leave the
      // creator usable; measurement simply remains off for this page load.
      aiLayoutAnalytics = null;
    }
    window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'session:active', { 
      sessionId: currentSessionId,
      shortId: getShortSessionId(currentSessionId)
    });
    
    await fetchTaxonomy();
    
    // Try to restore saved state for this session
    // Session and photo persistence are local to this browser for 30 days.
    const restored = await restoreStateFromStorage();
    if (restored) {
      // Resume where the user left off rather than dropping them on step 1.
      // restoreStateFromStorage selected the saved stage after restoring its photo.
      if (!TEXT_MODE && state.step === 'brief' && !state.photoRead && state.imageFile) startPhotoRead();
      window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'restored-from-storage', {
        sessionId: getShortSessionId(currentSessionId),
        step: state.step,
        items: state.items.length
      });
      // Keep an immutable choice on reload; older inventory-only sessions get a fresh preview.
      if (state.items && state.items.length && !state.optionSet && !state.descriptionDirty && state.step === 'items') {
        state.optimising = true;
        runOptimiser().catch(() => {}).then(() => { state.optimising = false; render(); });
      }
    }

    render();
    if (state.optionSet) void saveAccountOptions();
    if (window.mdAiSession) {
      void window.mdAiSession.flush(VISION_URL);
      window.addEventListener('online', () => window.mdAiSession.flush(VISION_URL));
      window.addEventListener('pageshow', event => {
        if (event.persisted) { state.generatingUrl = false; render(); void window.mdAiSession.flush(VISION_URL); }
      });
    }
    window.MD_DEBUG_LAYOUT && console.log('[AI-LAYOUT]', 'render:initial');
  });
})();
